/**
 * AutoInvestigateCard — Floating card for one-click V2 investigation.
 *
 * Fires 6 independent investigation endpoints in parallel, streams results
 * progressively into accordion sections. Follows AnalyzeGraphCard pattern
 * (snap-to-corner, minimize, keyboard shortcut I).
 *
 * NEW capability — labeled prominently for demo.
 */
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { logEdge } from '../../utils/canvasDebug';
import {
    Wand2, Brain, Gauge, Users, GitBranchPlus, Layers, Database,
    GripVertical, X, Loader2, ChevronDown, ChevronRight, Minimize2,
    CheckCircle2, AlertCircle, RefreshCw, XCircle, Play, FileText,
    Pencil, Check, Eye, Save, Network, Building2, Settings, ArrowUpRight,
    AlertTriangle, ShieldCheck, Repeat,
} from 'lucide-react';
import { clsx } from 'clsx';
import { toast } from 'sonner';
import { INVESTIGATION_KEYS } from '../../hooks/useAutoInvestigation';
import type {
    UseAutoInvestigationReturn,
    InvestigationKey,
} from '../../hooks/useAutoInvestigation';
import { generateInvestigationReportHtml, generateOnePageSummaryHtml, openReportHtmlInNewTab, buildExecutiveSummary } from './AutoInvestigateReport';
import { syncInvestigationToCanvas, clearInvestigationHighlighting, computeEdgeLabel, normalizeCanvasAddr, normalizeEntityType, reconcileEdgeVisibility } from '../../utils/canvasSync';
import { CanvasNodeIntent, CanvasEdgeIntent } from '../../types/canvasIntent';
import { applyCanvasIntent } from '../../utils/applyCanvasIntent';
import { enrichExpandedTransactions } from '../../utils/enrichExpandedTransactions';
import { IntelligenceNoteView } from '../ui/IntelligenceNoteView';
import { REPORT_TYPES, buildReportTitle } from '../../utils/reportArtifacts';
import { inferRoleFromStep } from '../../constants/placementRoles';
import { isPlaceholderNode } from '../../utils/placeholderNode';
import { useGraphStore } from '../../stores/useGraphStore';
import { useTransactionStore, toExpandedTx } from '../../stores/useTransactionStore';
import { canvasController } from '../../services/canvasController';
import { VISIBILITY } from '../../constants/visibility';
import { resolveEdgeStyleKey } from '../../utils/edgeTypeVocabulary';
import { usePendingLayoutStore } from '../../stores/usePendingLayoutStore';
import { safeStr } from '../../utils/safeStr';
import { formatConsolidatedLabel, consolidateExistingEdges } from '../../utils/edgeConsolidation';

import { HopPathVisualization } from './HopPathVisualization';
import { InvestigationFeedback } from './InvestigationFeedback';
import { PromoteToCaseControl } from './PromoteToCaseControl';
import type { Core as CytoscapeCore } from 'cytoscape';
import { useInvestigationStore } from '../../stores/useInvestigationStore';
import { RestoredResultsBanner } from './RestoredResultsBanner';
import { useTriageStore } from '../../stores/useTriageStore';
import { useInvestigationCacheStore } from '../../stores/useInvestigationCacheStore';

type SnapCorner = 'TL' | 'BL' | 'BR';

// ── Exported constants for testability ──────────────────────────────
export const HOP_PRESETS = {
    /** Fast triage: 1-hop traces + hop-1 exposure (~25–40s warm). */
    quick:    { backward: 1, exposure: 1, forward: 1 },
    standard: { backward: 4, exposure: 3, forward: 4 },
    deep:     { backward: 6, exposure: 4, forward: 6 },
} as const;

export const BACKWARD_HOP_OPTIONS = [1, 3, 4, 6] as const;
export const EXPOSURE_HOP_OPTIONS = [1, 2, 3, 4] as const;
export const FORWARD_HOP_OPTIONS = [1, 3, 4, 6] as const;
export const DEFAULT_BACKWARD_HOPS = 4;
export const DEFAULT_EXPOSURE_HOPS = 3;
export const DEFAULT_FORWARD_HOPS = 4;
export const LS_KEY_BACKWARD_HOPS = 'auto_investigate_backward_hops';
export const LS_KEY_EXPOSURE_HOPS = 'auto_investigate_exposure_hops';
export const LS_KEY_FORWARD_HOPS = 'auto_investigate_forward_hops';

const SUPPORTED_CHAINS = [
    'ETH', 'TRX', 'BTC', 'BNB', 'OPBNB', 'MATIC', 'SOL', 'TON',
    'ARB', 'BASE', 'OP', 'AVAX', 'LINEA', 'BLAST',
    'SCROLL', 'GNOSIS', 'CELO', 'MANTLE', 'MOONBEAM', 'XRP',
] as const;

/**
 * AutoInvestigateCardProps — Phase 0 full: investigation + triage + visibility
 * now read from stores. Remaining props are callbacks needing Workbench context.
 */
interface AutoInvestigateCardProps {
    /** Override visibility — reads from useInvestigationStore by default */
    isVisible?: boolean;
    onClose?: () => void;
    chain: string | null;
    address: string | null;
    /** Override investigation — reads from useInvestigationStore by default */
    investigation?: UseAutoInvestigationReturn;
    /** Override triage — reads from useTriageStore by default */
    triageData?: any;
    onAddToGraph?: (elements: any[]) => void;
    onNotice?: () => void;
    onWatchlist?: () => void;
    onSave?: () => void;
    onPersistReport?: (payload: {
        title: string;
        htmlContent: string;
        metadata?: Record<string, any>;
    }) => Promise<void> | void;
    /** A previously-archived LE report for this address, if one exists.
     *  Surfaced as a "View saved report" link so the user can re-open the
     *  report after navigating away or reloading the investigation. */
    savedReport?: {
        id: string;
        title: string;
        generatedAt: string | null;
    } | null;
    /** Open the previously-archived report by its artifact id. */
    onOpenSavedReport?: (artifactId: string) => void | Promise<void>;
    cytoscapeInstance?: CytoscapeCore | null;
    dataMode?: 'mock' | 'cached' | 'live';
    onApplyFilterPreset?: (filters: Partial<import('../workbench/GraphFilterBar').GraphFilters>) => void;
    onEnrichElements?: (updates: Array<{ id: string; data: Record<string, any> }>) => void;
    /** Whether the card is embedded inside the Node Inspector (disables floating behavior) */
    isDocked?: boolean;
    /** Case the Workbench was opened from (URL `case_id`): runs carry it and "Promote to case" targets it. */
    caseId?: string | null;
}

const SNAP_POSITIONS: Record<SnapCorner, { top?: string; bottom?: string; left?: string; right?: string }> = {
    TL: { top: '80px', left: '16px' },
    BL: { bottom: '16px', left: '16px' },
    BR: { bottom: '16px', right: '420px' },
};

interface SectionDef {
    key: InvestigationKey;
    label: string;
    icon: React.FC<{ className?: string }>;
    description: string;
}

// Display order matches execution order (fast → slow) so sections light up top-to-bottom.
// The escalation banner at top provides instant verdict after completion.
const SECTIONS: SectionDef[] = [
    {
        key: 'exchange_detection',
        label: 'Exchange Detection',
        icon: Building2,
        description: 'Identifies known exchange wallets via entity DB, then behavioral fingerprinting.',
    },
    {
        key: 'velocity',
        label: 'Velocity Profile',
        icon: Gauge,
        description: 'Fund-through rate, dwell time, consolidation ratio, and velocity classification.',
    },
    {
        key: 'counterparty_triage',
        label: 'Top Counterparties',
        icon: Users,
        description: 'Ranks counterparties by transaction volume with risk tier assessment.',
    },
    {
        key: 'adaptive_transactions',
        label: 'Full Transaction Fetch',
        icon: Database,
        description: 'Signal-based adaptive fetch — loads beyond initial batch when patterns detected.',
    },
    {
        key: 'signal_assessment',
        label: 'Signal Assessment',
        icon: Brain,
        description: 'AI assessment with 8-family signal convergence analysis.',
    },
    {
        key: 'multi_hop_exposure',
        label: 'Multi-Hop Exposure',
        icon: Layers,
        description: 'Computes risk exposure across N hops with decay factor (configurable depth).',
    },
    {
        key: 'backward_trace',
        label: 'Backward Trace',
        icon: GitBranchPlus,
        description: 'Traces fund sources backward (configurable depth), stopping at exchanges/VASPs.',
    },
    {
        key: 'forward_trace',
        label: 'Forward Trace',
        icon: ArrowUpRight,
        description: 'Traces fund destinations forward (configurable depth), critical for victim investigations.',
    },
    {
        key: 'compliant_exchange',
        label: 'Find Compliant Exchange',
        icon: ShieldCheck,
        description: 'Searching for Indian-responsive legal targets up to 10 hops deep.',
    },
];

/* ── One-line summaries per section ── */
function getSummaryLine(key: InvestigationKey, data: any): string {
    if (!data) return 'Complete';
    switch (key) {
        case 'compliant_exchange': {
            const count = data.paths?.length || 0;
            return count > 0 ? `${count} legal path(s) identified` : 'No compliant exchanges found in 10 hops';
        }
        case 'signal_assessment': {
            // The /explain endpoint returns { analysis: string, ... } or signal data
            if (data.signal_families) {
                const triggered = data.families_triggered ?? 0;
                const tested = data.families_tested ?? 8;
                const rec = (data.escalation_recommendation || '').replace(/_/g, ' ');
                return `${triggered}/${tested} families — ${rec}`;
            }
            if (data.analysis) return data.analysis.substring(0, 80) + '...';
            return 'Assessment complete';
        }
        case 'velocity': {
            const profile = data.velocity_profile || 'Unknown';
            const m = data.metrics || {};
            const ftr = typeof (m.fund_through_rate ?? data.fund_through_rate) === 'number'
                ? `FTR ${(m.fund_through_rate ?? data.fund_through_rate).toFixed(2)}` : '';
            const dwell = typeof (m.dwell_time_hours ?? data.dwell_time_hours) === 'number'
                ? `dwell ${(m.dwell_time_hours ?? data.dwell_time_hours).toFixed(1)}h` : '';
            return [profile, ftr, dwell].filter(Boolean).join(' — ');
        }
        case 'counterparty_triage': {
            const list = data.counterparties || data.ranked || [];
            const highRisk = list.filter((c: any) => c.risk_tier === 'high' || (c.risk_score ?? 0) > 0.7).length;
            return `${list.length} ranked${highRisk > 0 ? `, ${highRisk} high-risk` : ''}`;
        }
        case 'backward_trace': {
            const paths = data.paths || data.traces || [];
            const status = data.status || '';
            const prog = data.progress || {};
            const fetched = prog.addresses_fetched || data.total_nodes || 0;
            if (status === 'partial') {
                return `Partial: ${fetched} addresses, ${paths.length} sources — re-run to continue`;
            }
            if (status === 'timeout') {
                return `Timed out — cached data available, re-run to continue`;
            }
            if (paths.length > 0) {
                return `${paths.length} source paths (${fetched} addresses explored)`;
            }
            return fetched > 0 ? `${fetched} addresses explored, no exchange found` : 'No paths found';
        }
        case 'forward_trace': {
            const paths = data.paths || data.traces || [];
            const status = data.status || '';
            const prog = data.progress || {};
            const fetched = prog.addresses_fetched || data.total_nodes || 0;
            if (status === 'partial') {
                return `Partial: ${fetched} addresses, ${paths.length} destinations — re-run to continue`;
            }
            if (status === 'timeout') {
                return `Timed out — cached data available, re-run to continue`;
            }
            if (paths.length > 0) {
                return `${paths.length} destination paths (${fetched} addresses explored)`;
            }
            return fetched > 0 ? `${fetched} addresses explored, no exchange found` : 'No paths found';
        }
        case 'multi_hop_exposure': {
            const unk = data.unknown_pct_by_hop;
            if (unk && typeof unk === 'object') {
                const hop1 = unk['1'] ?? unk[1];
                return typeof hop1 === 'number' ? `${hop1.toFixed(0)}% unattributed at hop 1` : 'Exposure computed';
            }
            if (typeof data.combined_risk_score === 'number') {
                return `Combined risk: ${(data.combined_risk_score * 100).toFixed(0)}%`;
            }
            return 'Exposure computed';
        }
        case 'adaptive_transactions': {
            const total = data.total_fetched ?? data.total ?? 0;
            const rounds = data.fetch_rounds ?? 1;
            const reason = data.stop_reason || '';
            if (reason === 'all_fetched') {
                return `${total} txs (all transactions on-chain)`;
            }
            return `${total} txs across ${rounds} round${rounds !== 1 ? 's' : ''}${reason ? ` (${reason})` : ''}`;
        }
        case 'exchange_detection': {
            if (data.is_exchange) {
                const name = data.exchange_name ? `${data.exchange_name} ` : '';
                const type = data.exchange_type ? `(${data.exchange_type.replace('_', ' ')})` : '';
                const src = data.source === 'entity_db' ? ' · confirmed' : ` · ${(data.confidence * 100).toFixed(0)}% confidence`;
                return `${name}${type}${src}`;
            }
            return `Not an exchange (${(data.confidence * 100).toFixed(0)}% confidence)`;
        }
        default:
            return 'Complete';
    }
}

/* ── Expanded detail per section ── */
function renderDetail(key: InvestigationKey, data: any): React.ReactNode {
    if (!data) return <p className="text-sm text-muted-foreground">No data available.</p>;

    switch (key) {
        case 'signal_assessment': {
            if (data.signal_families) {
                const families = data.signal_families as Record<string, any>;
                return (
                    <div className="space-y-1">
                        {Object.entries(families).map(([name, info]: [string, any]) => (
                            <div key={name} className="flex items-center gap-2 text-sm">
                                <span className={clsx(
                                    'w-2 h-2 rounded-full shrink-0',
                                    info.triggered === true ? 'bg-red-500' :
                                        info.triggered === false ? 'bg-emerald-500' : 'bg-gray-400'
                                )} />
                                <span className="text-muted-foreground capitalize">
                                    {name.replace(/_/g, ' ')}
                                </span>
                                {info.triggered === true && info.signals?.length > 0 && (
                                    <span className="text-foreground font-medium">
                                        {info.signals.slice(0, 3).join(', ')}
                                    </span>
                                )}
                            </div>
                        ))}
                        {data.score_contradictions?.length > 0 && (
                            <div className="mt-1.5 p-1.5 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-600 dark:text-amber-400">
                                {data.score_contradictions[0].description || data.score_contradictions[0]}
                            </div>
                        )}
                    </div>
                );
            }
            if (data.analysis) {
                return (
                    <div>
                        <div className="flex items-center gap-1.5 mb-1.5">
                            <span className="px-1.5 py-0.5 text-[10px] bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/20 rounded font-medium">AI-GENERATED</span>
                            <span className="text-[10px] text-muted-foreground">Gemini 2.0 Flash / GPT-4o-mini</span>
                        </div>
                        <IntelligenceNoteView
                            markdown={String(data.analysis)}
                            maxHeightClass="max-h-[240px]"
                        />
                    </div>
                );
            }
            return <p className="text-sm text-muted-foreground">Assessment data not structured.</p>;
        }

        case 'velocity': {
            const vm = data.metrics || {};
            const metrics = [
                { label: 'Fund-Through Rate', desc: 'Outflow / inflow ratio — 1.0 = pass-through', value: vm.fund_through_rate ?? data.fund_through_rate, fmt: (v: number) => v.toFixed(3) },
                { label: 'Consolidation Ratio', desc: 'Fraction of txs with 5+ inputs (aggregation)', value: vm.consolidation_ratio ?? data.consolidation_ratio, fmt: (v: number) => v.toFixed(3) },
                { label: 'Value Amplification', desc: 'Largest tx / median tx value', value: vm.value_amplification ?? data.value_amplification, fmt: (v: number) => v.toFixed(3) },
                { label: 'Dwell Time', desc: 'Median hours funds stay before moving out', value: vm.dwell_time_hours ?? data.dwell_time_hours, fmt: (v: number) => `${v.toFixed(1)}h` },
                { label: 'Regularity Score', desc: 'Daily tx count consistency (lower = erratic)', value: vm.regularity_score ?? data.regularity_score, fmt: (v: number) => v.toFixed(3) },
                { label: 'Burst Score', desc: 'Peak day / average day activity ratio', value: vm.burst_score ?? data.burst_score, fmt: (v: number) => v.toFixed(3) },
            ];
            return (
                <div className="space-y-1">
                    <div className="flex items-center gap-2 mb-1.5">
                        <span className={clsx(
                            'px-1.5 py-0.5 rounded text-xs font-bold uppercase',
                            data.velocity_profile === 'Relay' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' :
                                data.velocity_profile === 'Distributor' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' :
                                    data.velocity_profile === 'Accumulator' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-primary' :
                                        'bg-secondary text-muted-foreground'
                        )}>
                            {data.velocity_profile || 'Unknown'}
                        </span>
                        {typeof data.velocity_risk_score === 'number' && (
                            <span className="text-xs text-muted-foreground">
                                Risk: {(data.velocity_risk_score * 100).toFixed(0)}%
                            </span>
                        )}
                    </div>
                    {metrics.map(m => typeof m.value === 'number' ? (
                        <div key={m.label} className="flex justify-between text-sm items-start gap-2">
                            <div className="min-w-0">
                                <span className="text-muted-foreground">{m.label}</span>
                                <span className="block text-[10px] text-muted-foreground/70 leading-tight">{m.desc}</span>
                            </div>
                            <span className="font-mono text-foreground shrink-0">{m.fmt(m.value)}</span>
                        </div>
                    ) : null)}
                </div>
            );
        }

        case 'counterparty_triage': {
            const list = (data.counterparties || data.ranked || []).slice(0, 10);
            if (list.length === 0) return <p className="text-sm text-muted-foreground">No counterparties found.</p>;
            return (
                <div className="space-y-1">
                    {list.map((cp: any, idx: number) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground w-4 text-right shrink-0">{idx + 1}.</span>
                            <span className="font-mono text-foreground truncate flex-1" title={cp.address}>
                                {cp.address ? `${cp.address.slice(0, 8)}...${cp.address.slice(-6)}` : '?'}
                            </span>
                            {cp.volume !== undefined && (
                                <span className="text-muted-foreground shrink-0">
                                    {typeof cp.volume === 'number' ? cp.volume.toFixed(4) : cp.volume}
                                </span>
                            )}
                            <span className={clsx(
                                'px-1 py-0 rounded text-[10px] font-bold uppercase shrink-0',
                                cp.risk_tier === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' :
                                    cp.risk_tier === 'medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' :
                                        'bg-secondary text-muted-foreground'
                            )}>
                                {safeStr(cp.risk_tier) || safeStr(cp.entity_type) || 'unknown'}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }

        case 'backward_trace': {
            const paths = data.paths || data.traces || [];
            const status = data.status || '';
            const prog = data.progress || {};

            // Progress metadata bar (shown for all statuses)
            const progressBar = (prog.addresses_fetched || prog.cache_hits || prog.api_calls) ? (
                <div className="space-y-0.5 mb-1.5">
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                        {typeof prog.addresses_fetched === 'number' && (
                            <span>Fetched: <span className="font-mono text-foreground">{prog.addresses_fetched}</span></span>
                        )}
                        {typeof prog.cache_hits === 'number' && (
                            <span>Cache hits: <span className="font-mono text-foreground">{prog.cache_hits}</span></span>
                        )}
                        {typeof prog.api_calls === 'number' && (
                            <span>API calls: <span className="font-mono text-foreground">{prog.api_calls}</span></span>
                        )}
                        {typeof prog.elapsed_seconds === 'number' && (
                            <span>Time: <span className="font-mono text-foreground">{prog.elapsed_seconds}s</span></span>
                        )}
                    </div>
                    {typeof prog.addresses_queued === 'number' && prog.addresses_queued > 0 && (
                        <div className="text-xs text-amber-600 dark:text-amber-400">
                            {prog.addresses_queued} addresses remaining
                            {typeof prog.estimated_remaining_seconds === 'number' && prog.estimated_remaining_seconds > 0 && (
                                <> (~{prog.estimated_remaining_seconds}s est.)</>
                            )}
                        </div>
                    )}
                    {prog.limit_reached && (
                        <div className="text-xs text-amber-600 dark:text-amber-400">
                            Address limit reached (200 max)
                        </div>
                    )}
                </div>
            ) : null;

            // Partial/timeout banner
            const statusBanner = (status === 'partial' || status === 'timeout') ? (
                <div className={clsx(
                    'p-1.5 rounded text-xs mb-1.5 border',
                    status === 'partial'
                        ? 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400'
                        : 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400'
                )}>
                    {data.message || (status === 'partial'
                        ? 'Partial results — re-run to continue from cached data.'
                        : 'Timed out — re-run to continue from cached data.')}
                </div>
            ) : null;

            // Exploration summary context
            const expSummary = data.exploration_summary;
            const stopReasonLabel: Record<string, string> = {
                depth: 'Max hop depth reached',
                limit_reached: 'Address limit reached (200)',
                stop_entity: 'Source entity found',
                no_entity_coverage: 'Entity DB coverage insufficient in this neighborhood',
                time_budget: 'Time budget reached — re-run uses cached data for faster results',
            };

            if (paths.length === 0 && !statusBanner) {
                return (
                    <div className="text-sm text-muted-foreground space-y-1">
                        {progressBar}
                        <p>No source paths found within hop limit.</p>
                        {expSummary ? (
                            <div className="text-xs space-y-0.5 p-1.5 bg-secondary/50 rounded border border-border">
                                <p>Explored <span className="font-mono text-foreground">{expSummary.total_nodes_explored}</span> addresses across <span className="font-mono text-foreground">{expSummary.bfs_depth_reached}</span> hops</p>
                                {expSummary.stop_reason && (
                                    <p className="text-amber-600 dark:text-amber-400">{stopReasonLabel[expSummary.stop_reason] || expSummary.stop_reason}</p>
                                )}
                            </div>
                        ) : (
                            data.total_nodes !== undefined && <p>{data.total_nodes} nodes explored.</p>
                        )}
                    </div>
                );
            }

            return (
                <div className="space-y-1.5">
                    {statusBanner}
                    {progressBar}
                    {paths.length > 0 && (
                        <div className="space-y-1">
                            {paths.slice(0, 5).map((path: any, idx: number) => (
                                <HopPathVisualization
                                    key={idx}
                                    path={path.addresses || path.path || []}
                                    hopDetails={path.hop_details || []}
                                    edges={path.edges || []}
                                    sourceEntity={path.source}
                                    compact
                                />
                            ))}
                            {paths.length > 5 && (
                                <p className="text-xs text-muted-foreground">+{paths.length - 5} more paths</p>
                            )}
                            {expSummary && expSummary.total_nodes_explored > (data.total_nodes || 0) && (
                                <p className="text-xs text-muted-foreground">
                                    Scope: {expSummary.total_nodes_explored} addresses explored, {paths.length} paths shown
                                </p>
                            )}
                        </div>
                    )}
                    {data.source_entities?.length > 0 && !paths.length && (
                        <div className="space-y-0.5">
                            <span className="text-xs text-muted-foreground uppercase font-bold">Source entities found</span>
                            {data.source_entities.map((e: any, i: number) => (
                                <div key={i} className="flex items-center gap-2 text-sm">
                                    <span className="font-mono text-foreground truncate" title={e.address}>
                                        {e.address?.slice(0, 8)}...{e.address?.slice(-4)}
                                    </span>
                                    <span className="text-primary text-xs font-medium">
                                        {safeStr(e.entity_name)} ({safeStr(e.entity_type)})
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        case 'forward_trace': {
            const paths = data.paths || data.traces || [];
            const status = data.status || '';
            const prog = data.progress || {};

            // Progress metadata bar (shown for all statuses)
            const progressBar = (prog.addresses_fetched || prog.cache_hits || prog.api_calls) ? (
                <div className="space-y-0.5 mb-1.5">
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                        {typeof prog.addresses_fetched === 'number' && (
                            <span>Fetched: <span className="font-mono text-foreground">{prog.addresses_fetched}</span></span>
                        )}
                        {typeof prog.cache_hits === 'number' && (
                            <span>Cache hits: <span className="font-mono text-foreground">{prog.cache_hits}</span></span>
                        )}
                        {typeof prog.api_calls === 'number' && (
                            <span>API calls: <span className="font-mono text-foreground">{prog.api_calls}</span></span>
                        )}
                        {typeof prog.elapsed_seconds === 'number' && (
                            <span>Time: <span className="font-mono text-foreground">{prog.elapsed_seconds}s</span></span>
                        )}
                    </div>
                    {typeof prog.addresses_queued === 'number' && prog.addresses_queued > 0 && (
                        <div className="text-xs text-amber-600 dark:text-amber-400">
                            {prog.addresses_queued} addresses remaining
                            {typeof prog.estimated_remaining_seconds === 'number' && prog.estimated_remaining_seconds > 0 && (
                                <> (~{prog.estimated_remaining_seconds}s est.)</>
                            )}
                        </div>
                    )}
                    {prog.limit_reached && (
                        <div className="text-xs text-amber-600 dark:text-amber-400">
                            Address limit reached (200 max)
                        </div>
                    )}
                </div>
            ) : null;

            // Partial/timeout banner
            const statusBanner = (status === 'partial' || status === 'timeout') ? (
                <div className={clsx(
                    'p-1.5 rounded text-xs mb-1.5 border',
                    status === 'partial'
                        ? 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400'
                        : 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400'
                )}>
                    {data.message || (status === 'partial'
                        ? 'Partial results — re-run to continue from cached data.'
                        : 'Timed out — re-run to continue from cached data.')}
                </div>
            ) : null;

            // Exploration summary context
            const expSummary = data.exploration_summary;
            const stopReasonLabel: Record<string, string> = {
                depth: 'Max hop depth reached',
                limit_reached: 'Address limit reached (200)',
                stop_entity: 'Destination entity found',
                no_entity_coverage: 'Entity DB coverage insufficient in this neighborhood',
                time_budget: 'Time budget reached — re-run uses cached data for faster results',
            };

            if (paths.length === 0 && !statusBanner) {
                return (
                    <div className="text-sm text-muted-foreground space-y-1">
                        {progressBar}
                        <p>No destination paths found within hop limit.</p>
                        {expSummary ? (
                            <div className="text-xs space-y-0.5 p-1.5 bg-secondary/50 rounded border border-border">
                                <p>Explored <span className="font-mono text-foreground">{expSummary.total_nodes_explored}</span> addresses across <span className="font-mono text-foreground">{expSummary.bfs_depth_reached}</span> hops</p>
                                {expSummary.stop_reason && (
                                    <p className="text-amber-600 dark:text-amber-400">{stopReasonLabel[expSummary.stop_reason] || expSummary.stop_reason}</p>
                                )}
                            </div>
                        ) : (
                            data.total_nodes !== undefined && <p>{data.total_nodes} nodes explored.</p>
                        )}
                    </div>
                );
            }

            return (
                <div className="space-y-1.5">
                    {statusBanner}
                    {progressBar}
                    {paths.length > 0 && (
                        <div className="space-y-1">
                            {paths.slice(0, 5).map((path: any, idx: number) => (
                                <HopPathVisualization
                                    key={idx}
                                    path={path.addresses || path.path || []}
                                    hopDetails={path.hop_details || []}
                                    edges={path.edges || []}
                                    targetEntity={path.target_entity}
                                    compact
                                />
                            ))}
                            {paths.length > 5 && (
                                <p className="text-xs text-muted-foreground">+{paths.length - 5} more paths</p>
                            )}
                            {expSummary && expSummary.total_nodes_explored > (data.total_nodes || 0) && (
                                <p className="text-xs text-muted-foreground">
                                    Scope: {expSummary.total_nodes_explored} addresses explored, {paths.length} paths shown
                                </p>
                            )}
                        </div>
                    )}
                    {data.target_entities?.length > 0 && !paths.length && (
                        <div className="space-y-0.5">
                            <span className="text-xs text-muted-foreground uppercase font-bold">Destination entities found</span>
                            {data.target_entities.map((e: any, i: number) => (
                                <div key={i} className="flex items-center gap-2 text-sm">
                                    <span className="font-mono text-foreground truncate" title={e.address}>
                                        {e.address?.slice(0, 8)}...{e.address?.slice(-4)}
                                    </span>
                                    <span className="text-primary text-xs font-medium">
                                        {safeStr(e.entity_name)} ({safeStr(e.entity_type)})
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        case 'multi_hop_exposure': {
            const hops = data.unknown_pct_by_hop || {};
            const direct = data.direct_exposure || {};
            const indirect = data.indirect_exposure || {};
            return (
                <div className="space-y-1.5">
                    {typeof data.combined_risk_score === 'number' && (
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Combined Risk</span>
                            <span className="font-bold text-foreground">{(data.combined_risk_score * 100).toFixed(0)}%</span>
                        </div>
                    )}
                    {Object.keys(hops).length > 0 && (
                        <div className="space-y-0.5">
                            <span className="text-xs text-muted-foreground uppercase font-bold">Unattributed % by hop</span>
                            <span className="text-[10px] text-muted-foreground/70 block">Counterparties not matched to any known entity</span>
                            {Object.entries(hops).map(([hop, pct]: [string, any]) => (
                                <div key={hop} className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Hop {hop}</span>
                                    <span className="font-mono text-foreground">{typeof pct === 'number' ? `${pct.toFixed(0)}%` : pct}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {(direct.high_risk_pct !== undefined || indirect.high_risk_pct !== undefined) && (
                        <div className="space-y-0.5">
                            <span className="text-xs text-muted-foreground uppercase font-bold">High-risk exposure</span>
                            {typeof direct.high_risk_pct === 'number' && (
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Direct</span>
                                    <span className="font-mono text-foreground">{direct.high_risk_pct.toFixed(1)}%</span>
                                </div>
                            )}
                            {typeof indirect.high_risk_pct === 'number' && (
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Indirect</span>
                                    <span className="font-mono text-foreground">{indirect.high_risk_pct.toFixed(1)}%</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        }

        case 'adaptive_transactions': {
            return (
                <div className="space-y-1">
                    {data.total_fetched !== undefined && (
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Total fetched</span>
                            <span className="font-mono text-foreground">{data.total_fetched}</span>
                        </div>
                    )}
                    {data.fetch_rounds !== undefined && (
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Fetch rounds</span>
                            <span className="font-mono text-foreground">{data.fetch_rounds}</span>
                        </div>
                    )}
                    {data.stop_reason && (
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Stop reason</span>
                            <span className="text-foreground">{data.stop_reason}</span>
                        </div>
                    )}
                    {data.pattern_triggers && data.pattern_triggers.length > 0 && (
                        <div className="mt-1">
                            <span className="text-xs text-muted-foreground uppercase font-bold">Triggered patterns</span>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                                {data.pattern_triggers.map((p: string, i: number) => (
                                    <span key={i} className="px-1 py-0 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-medium">
                                        {p}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        case 'exchange_detection': {
            const isExchange = data.is_exchange;
            const signals = data.signals || {};
            const signalKeys = ['high_fan_in', 'high_fan_out', 'pass_through_rate', 'consolidation', 'gas_station', 'volume_throughput'] as const;
            return (
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                        <span className={clsx(
                            'px-1.5 py-0.5 rounded text-xs font-bold uppercase',
                            isExchange
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-primary'
                                : 'bg-secondary text-muted-foreground'
                        )}>
                            {isExchange ? (data.exchange_type?.replace('_', ' ') || 'Exchange') : 'Not Exchange'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                            {(data.confidence * 100).toFixed(0)}% confidence · {data.source}
                        </span>
                    </div>
                    {data.exchange_name && (
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Entity</span>
                            <span className="font-medium text-foreground">{data.exchange_name}</span>
                        </div>
                    )}
                    {data.evidence && (
                        <p className="text-xs text-muted-foreground leading-relaxed">{data.evidence}</p>
                    )}
                    {data.source === 'heuristic' && signalKeys.some(k => typeof signals[k] === 'number') && (
                        <div className="space-y-0.5 mt-1">
                            <span className="text-xs text-muted-foreground uppercase font-bold">Signal Scores</span>
                            {signalKeys.map(k => typeof signals[k] === 'number' ? (
                                <div key={k} className="flex items-center gap-2 text-xs">
                                    <div className="w-24 shrink-0 text-muted-foreground">{k.replace(/_/g, ' ')}</div>
                                    <div className="flex-1 bg-secondary rounded-full h-1">
                                        <div
                                            className={clsx('h-1 rounded-full', signals[k] >= 0.6 ? 'bg-primary' : 'bg-muted-foreground/40')}
                                            style={{ width: `${Math.round(signals[k] * 100)}%` }}
                                        />
                                    </div>
                                    <span className="w-8 text-right font-mono text-foreground">{(signals[k] * 100).toFixed(0)}%</span>
                                </div>
                            ) : null)}
                        </div>
                    )}
                </div>
            );
        }

        default:
            return <pre className="text-xs text-muted-foreground max-h-[200px] overflow-auto">{JSON.stringify(data, null, 2)}</pre>;
    }
}

/* ── Elapsed timer shown during loading ── */
function ElapsedTimer() {
    const [elapsed, setElapsed] = React.useState(0);
    React.useEffect(() => {
        const t0 = Date.now();
        const id = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
        return () => clearInterval(id);
    }, []);
    return <span className="font-mono text-xs text-muted-foreground ml-auto shrink-0">{elapsed}s</span>;
}

/* ── Status icon helper ── */
function StatusIcon({ status, degraded }: { status: string; degraded?: boolean }) {
    switch (status) {
        case 'loading':
            return <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />;
        case 'loaded':
            return degraded
                ? <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />;
        case 'error':
            return <AlertCircle className="w-3.5 h-3.5 text-red-500" />;
        default:
            return null;
    }
}

/**
 * Normalize address for canvas node IDs.  EVM addresses (0x...) are
 * lowercased for case-insensitive matching; TRX/BTC/SOL preserved.
 * Must match backend DiscoveryEmitter._norm() logic.
 */
/** @deprecated Use normalizeCanvasAddr from canvasSync.ts instead. Kept as alias for existing call sites. */
function normalizeCanvasAddress(address: string): string {
    return normalizeCanvasAddr(address);
}

/* ── Convert backward-trace response to Cytoscape elements ── */
/**
 * Select top counterparties for canvas display using adaptive thresholds.
 * Combines volume-based top-N + all exchanges + all high-risk entities.
 */
function selectTopCounterparties(counterparties: any[]): any[] {
    const total = counterparties.length;
    if (total === 0) return [];

    // Adaptive top-N by volume
    let topN: number;
    if (total <= 10) topN = Math.max(3, total);
    else if (total <= 50) topN = Math.max(5, Math.ceil(total * 0.10));
    else if (total <= 100) topN = Math.max(8, Math.ceil(total * 0.08));
    else topN = Math.min(25, Math.max(10, Math.ceil(total * 0.05)));

    // Sort by total volume descending
    const sorted = [...counterparties].sort((a, b) => {
        const volA = (a.volume_in || a.total_sent || 0) + (a.volume_out || a.total_received || 0);
        const volB = (b.volume_in || b.total_sent || 0) + (b.volume_out || b.total_received || 0);
        return volB - volA;
    });

    const selected = new Map<string, any>();

    // 1. Volume top-N
    sorted.slice(0, topN).forEach(cp => {
        const addr = cp.address || cp.counterparty;
        if (addr) selected.set(addr, cp);
    });

    // 2. All exchanges (always included)
    counterparties.forEach(cp => {
        if (cp.entity_type === 'exchange' || cp.entity_type === 'Exchange' || cp.entity_type === 'high_risk_exchange' || cp.entity_type === 'High_Risk_Exchange') {
            const addr = cp.address || cp.counterparty;
            if (addr) selected.set(addr, cp);
        }
    });

    // 3. All high-risk (correct tier names from backend)
    counterparties.forEach(cp => {
        if (['high', 'severe', 'critical'].includes(cp.risk_tier)) {
            const addr = cp.address || cp.counterparty;
            if (addr) selected.set(addr, cp);
        }
    });

    return Array.from(selected.values());
}

function convertTraceToElements(data: any): any[] {
    const elements: any[] = [];
    const nodes = data.nodes || [];
    const edges = data.edges || [];
    for (const n of nodes) {
        elements.push({
            data: {
                id: n.id,
                label: n.entity_name || `${n.id.slice(0, 6)}...${n.id.slice(-4)}`,
                type: 'address',
                entity_name: n.entity_name || null,
                entity_type: n.entity_type || null,
                risk_score: n.risk_score ?? null,
            },
        });
    }
    for (const e of edges) {
        elements.push({
            data: {
                id: `${e.from}-${e.to}-${e.tx_hash || 'trace'}`,
                source: e.from,
                target: e.to,
                label: (e.value != null && e.value !== 0) ? String(e.value) : '',
                value: (e.value != null && e.value !== 0) ? e.value : null,
                token_symbol: e.token_symbol || null,
                timestamp: e.timestamp || null,
                tx_hash: e.tx_hash || null,
            },
        });
    }
    return elements;
}

export const AutoInvestigateCard: React.FC<AutoInvestigateCardProps> = (props) => {
    // Read from stores with prop override (Phase 0 full)
    const storeVisible = useInvestigationStore(s => s.showAutoInvestigateCard);
    const storeInvestigation = useInvestigationStore(s => s.investigation);
    const storeTriageData = useTriageStore(s => s.triageData);

    const isVisible = props.isVisible ?? storeVisible;
    const onClose = props.onClose ?? (() => useInvestigationStore.getState().setShowAutoInvestigateCard(false));
    const { chain, address } = props;
    const investigation = props.investigation ?? storeInvestigation;
    const triageData = props.triageData ?? storeTriageData;
    const {
        onAddToGraph, onNotice, onWatchlist, onSave, onPersistReport,
        savedReport, onOpenSavedReport,
        cytoscapeInstance, onApplyFilterPreset, onEnrichElements,
    } = props;
    const caseId = props.caseId ?? null;
    const dataMode = props.dataMode ?? 'live';

    const malformedSectionWarningRef = useRef<string | null>(null);

    // Destructure with safe defaults when investigation is null (before store sync)
    // and when a saved investigation snapshot is partial/malformed.
    // IMPORTANT: if investigation exists but keys are missing, surface as explicit
    // error state (not silent idle) so the issue is visible and debuggable.
    const sections = useMemo(() => {
        const incoming = investigation?.sections as Partial<UseAutoInvestigationReturn['sections']> | undefined;
        const hasInvestigationObject = !!investigation;
        const normalized = {} as UseAutoInvestigationReturn['sections'];
        const missingKeys: InvestigationKey[] = [];

        for (const key of INVESTIGATION_KEYS) {
            const section = incoming?.[key];
            if (section) {
                normalized[key] = section;
            } else if (hasInvestigationObject) {
                missingKeys.push(key);
                normalized[key] = {
                    status: 'error',
                    error: `Internal state mismatch: missing section "${key}". Please re-run investigation.`,
                };
            } else {
                normalized[key] = { status: 'idle' };
            }
        }

        if (hasInvestigationObject && missingKeys.length > 0) {
            const warningKey = missingKeys.join(',');
            if (malformedSectionWarningRef.current !== warningKey) {
                malformedSectionWarningRef.current = warningKey;
                console.error(
                    '[AutoInvestigateCard] Malformed investigation state: missing section keys.',
                    { missingKeys, availableKeys: Object.keys(incoming || {}) },
                );
                Sentry.captureMessage('[AutoInvestigateCard] Malformed investigation state: missing section keys', {
                    level: 'warning',
                    extra: {
                        missingKeys,
                        availableKeys: Object.keys(incoming || {}),
                    },
                });
            }
        }

        return normalized;
    }, [investigation, investigation?.sections]);
    const isRunning = investigation?.isRunning ?? false;
    const isComplete = investigation?.isComplete ?? false;
    const prefetchStatus = investigation?.prefetchStatus ?? ('idle' as const);
    const progress = investigation?.progress ?? { completed: 0, total: 0 };
    const run = investigation?.run ?? ((() => {}) as UseAutoInvestigationReturn['run']);
    const runViaStream = investigation?.runViaStream ?? ((() => {}) as UseAutoInvestigationReturn['runViaStream']);
    const refresh = investigation?.refresh ?? ((() => {}) as UseAutoInvestigationReturn['refresh']);
    const cancel = investigation?.cancel ?? (() => {});
    const reset = investigation?.reset ?? (() => {});
    const hydrate = investigation?.hydrate ?? (() => {});

    // R24 stability rule: the SSE flush path adds elements but never triggers a
    // relayout. The layout hold set in handleRun is released one step at a time
    // (a step reaching a final status) and dropped when the run stops.
    const streamLayoutHeldRef = useRef(false);
    const streamSawRunningRef = useRef(false);
    const finishedStepSignature = INVESTIGATION_KEYS
        .filter((k) => {
            const st = sections[k]?.status;
            return st !== undefined && st !== 'idle' && st !== 'loading';
        })
        .join(',');
    useEffect(() => {
        if (!streamLayoutHeldRef.current) return;
        usePendingLayoutStore.getState().releaseLayout();
    }, [finishedStepSignature]);
    useEffect(() => {
        if (isRunning) {
            streamSawRunningRef.current = true;
            return;
        }
        if (streamLayoutHeldRef.current && streamSawRunningRef.current) {
            streamLayoutHeldRef.current = false;
            streamSawRunningRef.current = false;
            usePendingLayoutStore.getState().endStreamHold();
        }
    }, [isRunning]);
    useEffect(() => () => {
        if (streamLayoutHeldRef.current) {
            streamLayoutHeldRef.current = false;
            usePendingLayoutStore.getState().endStreamHold();
        }
    }, []);
    const exportForSave = investigation?.exportForSave ?? (() => null);
    // Circulation / pass-through findings (cycle_detector) — not an
    // INVESTIGATION_KEYS section; surfaced separately below the section list.
    const circulation = investigation?.circulation ?? null;
    // Asset-recovery bundle (seizure matrix, USD reconciliation, flow diagrams)
    // — orchestrator post-trace passes; surfaced in the report like circulation.
    const assetRecovery = investigation?.assetRecovery ?? null;
    const circByPivot = useMemo(() => {
        const groups = new Map<string, any[]>();
        for (const f of (circulation?.findings ?? [])) {
            const pivot = (f.pivots && f.pivots[0]) || f.terminus || f.origin || 'unknown';
            if (!groups.has(pivot)) groups.set(pivot, []);
            groups.get(pivot)!.push(f);
        }
        const maxUsd = (fs: any[]) => Math.max(0, ...fs.map(f => f.bottleneck_usd || 0));
        return Array.from(groups.entries()).sort((a, b) => maxUsd(b[1]) - maxUsd(a[1]));
    }, [circulation]);
    const [snap, setSnap] = useState<SnapCorner>('TL');
    const [isMinimized, setIsMinimized] = useState(false);
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const cardRef = useRef<HTMLDivElement>(null);

    // Root address — internal state, synced from props, user-editable
    const [targetChain, setTargetChain] = useState<string>(chain || 'ETH');
    const [targetAddress, setTargetAddress] = useState<string>(address || '');
    const [isEditing, setIsEditing] = useState(false);
    const [editChain, setEditChain] = useState<string>(chain || 'ETH');
    const [editAddress, setEditAddress] = useState<string>(address || '');

    // Importance filter for canvas edges (0.0 to 1.0)
    const [importanceFilter, setImportanceFilter] = useState<number>(0.2);

    // Backward trace "Add to Graph" guard
    const [traceAdded, setTraceAdded] = useState(false);
    const [forwardTraceAdded, setForwardTraceAdded] = useState(false);
    const [counterpartiesAdded, setCounterpartiesAdded] = useState(false);
    const [isSavingReport, setIsSavingReport] = useState(false);
    const [execSummaryOpen, setExecSummaryOpen] = useState(false);
    const [nextStepsExpanded, setNextStepsExpanded] = useState(false);

    // Issue B — when user has clicked a different node but investigation
    // is mid-run or already has results, the card keeps showing the old
    // address.  Track this so we can render a banner offering "Switch to
    // {newAddress}".  We also use this check to know whether to silently
    // reset (idle case) or show the banner (busy/complete case).
    const nodeMismatch = !!(
        address && address !== targetAddress
        && (isRunning || isComplete)
    );

    // When true, after sticky target catches up to the selected node, auto-run.
    const pendingInvestigateSelectedRef = useRef(false);
    const lastInvestigateRequestSeqRef = useRef(0);
    // Declared early — right-click retarget + completion sync both clear this.
    const completionSyncedRef = useRef(false);

    // Right-click / external "Re-run Auto-Investigate for this address".
    // Sequential SSE only (runViaStream); cancel in-flight first; never parallelize.
    const investigateRequest = useInvestigationStore(s => s.investigateRequest);
    useEffect(() => {
        if (!investigateRequest) return;
        if (investigateRequest.seq === lastInvestigateRequestSeqRef.current) return;
        lastInvestigateRequestSeqRef.current = investigateRequest.seq;

        const addr = investigateRequest.address;
        const ch = investigateRequest.chain || chain || targetChain;
        if (!addr) {
            useInvestigationStore.getState().clearInvestigateRequest();
            return;
        }

        // Cancellation-safe: stop any in-flight sequential stream before retargeting.
        if (isRunning) {
            cancel();
        }

        setTargetChain(ch || 'ETH');
        setTargetAddress(addr);
        setEditChain(ch || 'ETH');
        setEditAddress(addr);
        reset();
        setTraceAdded(false);
        setForwardTraceAdded(false);
        setCounterpartiesAdded(false);
        setExpandedKey(null);
        setIsEditing(false);
        setNextStepsExpanded(false);
        completionSyncedRef.current = false;
        pendingInvestigateSelectedRef.current = true;
        useInvestigationStore.getState().clearInvestigateRequest();
    }, [
        investigateRequest,
        chain,
        targetChain,
        isRunning,
        cancel,
        reset,
    ]);

    // Manual switch — cache current run, then restore selected node's cache or reset.
    const handleSwitchToSelectedNode = useCallback((opts?: { forceFresh?: boolean }) => {
        if (!address || !chain) return;

        if (isComplete && targetAddress && targetChain) {
            const saved = exportForSave();
            if (saved) {
                useInvestigationCacheStore.getState().setEntry(
                    targetChain,
                    targetAddress,
                    saved,
                    circulation,
                );
            }
        }

        setTargetChain(chain);
        setTargetAddress(address);
        setEditChain(chain);
        setEditAddress(address);

        const cached = !opts?.forceFresh
            ? useInvestigationCacheStore.getState().getEntry(chain, address)
            : null;
        if (cached?.sections) {
            hydrate(cached.sections as any, {
                address: cached.address,
                circulation: cached.circulation ?? null,
            });
        } else {
            reset();
        }

        setTraceAdded(false);
        setForwardTraceAdded(false);
        setCounterpartiesAdded(false);
        setExpandedKey(null);
        setIsEditing(false);
        setNextStepsExpanded(false);
        completionSyncedRef.current = false;
    }, [chain, address, reset, hydrate, exportForSave, isComplete, targetAddress, targetChain, circulation]);

    // One-click: retarget sticky investigation root to the selected canvas node
    // and start Auto-Investigate (Phase 2 per-node UX).
    const handleInvestigateSelectedNode = useCallback(() => {
        if (!address || !chain || isRunning) return;
        pendingInvestigateSelectedRef.current = true;
        handleSwitchToSelectedNode({ forceFresh: true });
    }, [address, chain, isRunning, handleSwitchToSelectedNode]);

    // Persist completed runs so switching nodes does not lose the report.
    useEffect(() => {
        if (!isComplete || isRunning || !targetAddress || !targetChain) return;
        const saved = exportForSave();
        if (!saved) return;
        useInvestigationCacheStore.getState().setEntry(
            targetChain,
            targetAddress,
            saved,
            circulation,
        );
    }, [isComplete, isRunning, targetAddress, targetChain, sections, circulation, exportForSave]);

    // 🔄 SYNC TARGET: When props change (user selected a different node), update target.
    // Only reset if investigation is idle (not running, not complete with results).
    // This allows clicking nodes while investigation runs without losing progress.
    useEffect(() => {
        if (!chain || !address) return;
        if (address === targetAddress && chain === targetChain) return;

        // If investigation is running or has results, don't reset — just update the display
        if (isRunning || isComplete) {
            // Investigation in progress or has results — don't disrupt.
            // The mismatch banner (rendered below) tells the user what's
            // happening and offers a "Switch" action.
            return;
        }

        console.log('🔄 Target changed (idle), updating...', { from: targetAddress, to: address });

        // Update target address/chain
        setTargetChain(chain);
        setTargetAddress(address);
        setEditChain(chain);
        setEditAddress(address);

        const cached = useInvestigationCacheStore.getState().getEntry(chain, address);
        if (cached?.sections) {
            hydrate(cached.sections as any, {
                address: cached.address,
                circulation: cached.circulation ?? null,
            });
        } else {
            reset();
        }

        // Reset UI state
        setTraceAdded(false);
        setForwardTraceAdded(false);
        setCounterpartiesAdded(false);
        setExpandedKey(null);
        setIsEditing(false);
        setNextStepsExpanded(false);
        completionSyncedRef.current = false;
    }, [chain, address, targetChain, targetAddress, reset, hydrate, isRunning, isComplete]);

    // 🎨 CANVAS SYNC: When investigation completes, AUTO-ADD results to graph + apply styling
    useEffect(() => {
        if (!isComplete || !cytoscapeInstance || !targetAddress) return;
        // Only run completion sync once per investigation
        if (completionSyncedRef.current) return;
        completionSyncedRef.current = true;

        console.log('🎨 Auto-Investigation complete, adding results to graph...');

        // Step 1: AUTO-ADD investigation results to graph (if not already present)
        const elementsToAdd: any[] = [];

        // 1A. Add target address node if not on canvas
        if (!cytoscapeInstance.getElementById(targetAddress).length) {
            const exchangeData = sections.exchange_detection.data;
            elementsToAdd.push({
                data: {
                    id: targetAddress,
                    label: exchangeData?.is_exchange && exchangeData?.exchange_name
                        ? exchangeData.exchange_name
                        : `${targetAddress.slice(0, 6)}...${targetAddress.slice(-4)}`,
                    type: 'address',
                    chain: chain || undefined,
                    entity_type: exchangeData?.is_exchange ? 'Exchange' : undefined,
                    entity_name: exchangeData?.exchange_name || undefined,
                }
            });
            console.log('  → Adding target address to canvas');
        }

        // 1B. Auto-add backward trace path (if found)
        const backwardTrace = sections.backward_trace.data;
        if (backwardTrace?.paths && backwardTrace.paths.length > 0) {
            const primaryPath = backwardTrace.paths[0];
            const pathAddresses = primaryPath.path || primaryPath.addresses || [];

            // Build hop_details lookup for intermediate node entity labels
            const bwHopMap = new Map<string, any>();
            if (primaryPath.hop_details) {
                primaryPath.hop_details.forEach((hd: any) => {
                    const hdAddr = normalizeCanvasAddress(hd.address || '');
                    if (hdAddr && (hd.entity_name || hd.entity_type)) {
                        bwHopMap.set(hdAddr, hd);
                    }
                });
            }
            if (backwardTrace.nodes) {
                backwardTrace.nodes.forEach((n: any) => {
                    const nAddr = normalizeCanvasAddress(n.id || n.address || '');
                    if (nAddr && !bwHopMap.has(nAddr) && (n.entity_name || n.entity_type)) {
                        bwHopMap.set(nAddr, n);
                    }
                });
            }

            pathAddresses.forEach((rawAddr: string, idx: number) => {
                const addr = normalizeCanvasAddress(rawAddr);
                if (!cytoscapeInstance.getElementById(addr).length) {
                    const isSource = idx === pathAddresses.length - 1;
                    const sourceEntity = primaryPath.target_entity || primaryPath.source;
                    const hopInfo = bwHopMap.get(addr);
                    const entityName = isSource ? sourceEntity?.name : (hopInfo?.entity_name || undefined);
                    const entityType = isSource ? sourceEntity?.type : (hopInfo?.entity_type || undefined);
                    elementsToAdd.push({
                        data: {
                            id: addr,
                            label: entityName
                                ? entityName
                                : `${addr.slice(0, 6)}...${addr.slice(-4)}`,
                            type: 'address',
                            entity_type: entityType,
                            entity_name: entityName,
                            risk_score: hopInfo?.risk_score,
                        }
                    });
                }
            });

            // Build edge lookup from trace edges (if available) for metadata
            const traceEdgeMap = new Map<string, any>();
            if (backwardTrace.edges) {
                backwardTrace.edges.forEach((edge: any) => {
                    const s = normalizeCanvasAddress(edge.from || edge.source || edge.from_address || '');
                    const t = normalizeCanvasAddress(edge.to || edge.target || edge.to_address || '');
                    if (s && t) traceEdgeMap.set(`${s}-${t}`, edge);
                });
            }

            // Add edges between path nodes
            for (let i = 0; i < pathAddresses.length - 1; i++) {
                const source = normalizeCanvasAddress(pathAddresses[i]);
                const target = normalizeCanvasAddress(pathAddresses[i + 1]);
                const edgeId = `${source}-${target}`;
                if (!cytoscapeInstance.getElementById(edgeId).length) {
                    const traceEdge = traceEdgeMap.get(edgeId);
                    const edgeVal = traceEdge?.value || traceEdge?.amount;
                    elementsToAdd.push({
                        data: {
                            id: edgeId,
                            source,
                            target,
                            type: 'transaction',
                            label: edgeVal ? computeEdgeLabel(edgeVal, traceEdge?.token_symbol, traceEdge?.tx_count) : undefined,
                            tx_hash: traceEdge?.tx_hash || traceEdge?.hash,
                            value: edgeVal,
                            timestamp: traceEdge?.timestamp || traceEdge?.block_timestamp,
                            token_symbol: traceEdge?.token_symbol,
                            in_path: true,
                        }
                    });
                }
            }
            console.log(`  → Adding backward trace path (${pathAddresses.length} nodes)`);
        } else if (backwardTrace?.nodes && backwardTrace.nodes.length > 0) {
            // No complete path to exchange, but trace explored some nodes.
            // C4 FIX: Only add nodes that appear in at least one trace edge,
            // preventing orphan nodes from BFS-explored addresses with no
            // visible connections on canvas.
            const bwEdgeConnected = new Set<string>();
            const existingCanvasNodeIds = new Set<string>();
            cytoscapeInstance.nodes().forEach((n: any) => { existingCanvasNodeIds.add(n.id()); });

            if (backwardTrace.edges) {
                backwardTrace.edges.forEach((edge: any) => {
                    const s = normalizeCanvasAddress(edge.from || edge.source || edge.from_address || '');
                    const t = normalizeCanvasAddress(edge.to || edge.target || edge.to_address || '');
                    if (s && t) {
                        bwEdgeConnected.add(s);
                        bwEdgeConnected.add(t);
                    }
                });
            }

            let bwNodesAdded = 0;
            backwardTrace.nodes.forEach((node: any) => {
                const addr = normalizeCanvasAddress(node.id || node.address || '');
                if (!addr || cytoscapeInstance.getElementById(addr).length) return;
                // Only add if this address appears in at least one trace edge
                // or is already connected to an existing canvas node
                if (!bwEdgeConnected.has(addr) && !existingCanvasNodeIds.has(addr)) return;
                elementsToAdd.push({
                    data: {
                        id: addr,
                        label: node.entity_name || `${addr.slice(0, 6)}...${addr.slice(-4)}`,
                        type: 'address',
                        entity_type: node.entity_type,
                        entity_name: node.entity_name,
                        risk_score: node.risk_score,
                    }
                });
                bwNodesAdded++;
            });

            // Add edges from trace with full transaction metadata
            if (backwardTrace.edges) {
                backwardTrace.edges.forEach((edge: any) => {
                    const source = normalizeCanvasAddress(edge.from || edge.source || edge.from_address || '');
                    const target = normalizeCanvasAddress(edge.to || edge.target || edge.to_address || '');
                    const edgeId = edge.tx_hash || `${source}-${target}`;
                    if (source && target && !cytoscapeInstance.getElementById(edgeId).length) {
                        const edgeVal = edge.value || edge.amount;
                        elementsToAdd.push({
                            data: {
                                id: edgeId,
                                source,
                                target,
                                type: 'transaction',
                                label: computeEdgeLabel(edgeVal, edge.token_symbol, edge.tx_count),
                                tx_hash: edge.tx_hash || edge.hash,
                                value: edgeVal,
                                timestamp: edge.timestamp || edge.block_timestamp,
                                block_height: edge.block_height,
                                token_symbol: edge.token_symbol,
                                successful: edge.successful !== false,
                                importance: edge.importance || 'normal',
                                in_path: true,
                            }
                        });
                    }
                });
            }
            console.log(`  → Adding backward trace partial results (${bwNodesAdded}/${backwardTrace.nodes.length} connected nodes, no exchange found)`);
        }

        // 1C. Auto-add forward trace path (if found)
        const forwardTrace = sections.forward_trace.data;
        if (forwardTrace?.paths && forwardTrace.paths.length > 0) {
            const primaryPath = forwardTrace.paths[0];
            const pathAddresses = primaryPath.path || primaryPath.addresses || [];

            // Build node info lookup from trace nodes + hop_details (if available)
            const fwdNodeMap = new Map<string, any>();
            if (forwardTrace.nodes) {
                forwardTrace.nodes.forEach((n: any) => {
                    const nAddr = normalizeCanvasAddress(n.id || n.address || '');
                    if (nAddr) fwdNodeMap.set(nAddr, n);
                });
            }
            // Also pull entity info from hop_details (returned by find_paths_to_entity_types)
            if (primaryPath.hop_details) {
                primaryPath.hop_details.forEach((hd: any) => {
                    const hdAddr = normalizeCanvasAddress(hd.address || '');
                    if (hdAddr && !fwdNodeMap.has(hdAddr) && (hd.entity_name || hd.entity_type)) {
                        fwdNodeMap.set(hdAddr, hd);
                    }
                });
            }

            pathAddresses.forEach((rawAddr: string, idx: number) => {
                const addr = normalizeCanvasAddress(rawAddr);
                if (!cytoscapeInstance.getElementById(addr).length) {
                    const isDestination = idx === pathAddresses.length - 1;
                    const targetEntity = primaryPath.target_entity;
                    const nodeInfo = fwdNodeMap.get(addr);
                    // Use entity_name from: node info > target entity (if destination) > fallback
                    const entityName = nodeInfo?.entity_name || (isDestination ? targetEntity?.name : undefined);
                    const entityType = nodeInfo?.entity_type || (isDestination ? targetEntity?.type : undefined);
                    elementsToAdd.push({
                        data: {
                            id: addr,
                            label: entityName || `${addr.slice(0, 6)}...${addr.slice(-4)}`,
                            type: 'address',
                            entity_type: entityType,
                            entity_name: entityName,
                            risk_score: nodeInfo?.risk_score,
                        }
                    });
                }
            });

            // Build edge lookup from trace edges (if available) for metadata
            const fwdTraceEdgeMap = new Map<string, any>();
            if (forwardTrace.edges) {
                forwardTrace.edges.forEach((edge: any) => {
                    const s = normalizeCanvasAddress(edge.from || edge.source || edge.from_address || '');
                    const t = normalizeCanvasAddress(edge.to || edge.target || edge.to_address || '');
                    if (s && t) fwdTraceEdgeMap.set(`${s}-${t}`, edge);
                });
            }

            // Add edges between path nodes
            for (let i = 0; i < pathAddresses.length - 1; i++) {
                const source = normalizeCanvasAddress(pathAddresses[i]);
                const target = normalizeCanvasAddress(pathAddresses[i + 1]);
                const edgeId = `${source}-${target}`;
                if (!cytoscapeInstance.getElementById(edgeId).length) {
                    const traceEdge = fwdTraceEdgeMap.get(edgeId);
                    const edgeVal = traceEdge?.value || traceEdge?.amount;
                    elementsToAdd.push({
                        data: {
                            id: edgeId,
                            source,
                            target,
                            type: 'transaction',
                            label: edgeVal ? computeEdgeLabel(edgeVal, traceEdge?.token_symbol, traceEdge?.tx_count) : undefined,
                            tx_hash: traceEdge?.tx_hash || traceEdge?.hash,
                            value: edgeVal,
                            timestamp: traceEdge?.timestamp || traceEdge?.block_timestamp,
                            token_symbol: traceEdge?.token_symbol,
                            in_path: true,
                        }
                    });
                }
            }
            console.log(`  → Adding forward trace path (${pathAddresses.length} nodes)`);
        } else if (forwardTrace?.nodes && forwardTrace.nodes.length > 0) {
            // No complete path, but trace explored some nodes.
            // C4 FIX: Only add nodes that appear in at least one trace edge.
            const fwEdgeConnected = new Set<string>();
            if (forwardTrace.edges) {
                forwardTrace.edges.forEach((edge: any) => {
                    const s = normalizeCanvasAddress(edge.from || edge.source || edge.from_address || '');
                    const t = normalizeCanvasAddress(edge.to || edge.target || edge.to_address || '');
                    if (s && t) { fwEdgeConnected.add(s); fwEdgeConnected.add(t); }
                });
            }

            let fwNodesAdded = 0;
            forwardTrace.nodes.forEach((node: any) => {
                const addr = normalizeCanvasAddress(node.id || node.address || '');
                if (!addr || cytoscapeInstance.getElementById(addr).length) return;
                if (!fwEdgeConnected.has(addr)) return;
                elementsToAdd.push({
                    data: {
                        id: addr,
                        label: node.entity_name || `${addr.slice(0, 6)}...${addr.slice(-4)}`,
                        type: 'address',
                        entity_type: node.entity_type,
                        entity_name: node.entity_name,
                        risk_score: node.risk_score,
                    }
                });
                fwNodesAdded++;
            });

            // Add edges from trace with full transaction metadata
            if (forwardTrace.edges) {
                forwardTrace.edges.forEach((edge: any) => {
                    const source = normalizeCanvasAddress(edge.from || edge.source || edge.from_address || '');
                    const target = normalizeCanvasAddress(edge.to || edge.target || edge.to_address || '');
                    const edgeId = edge.tx_hash || `${source}-${target}`;
                    if (source && target && !cytoscapeInstance.getElementById(edgeId).length) {
                        const edgeVal = edge.value || edge.amount;
                        elementsToAdd.push({
                            data: {
                                id: edgeId,
                                source,
                                target,
                                type: 'transaction',
                                label: computeEdgeLabel(edgeVal, edge.token_symbol, edge.tx_count),
                                tx_hash: edge.tx_hash || edge.hash,
                                value: edgeVal,
                                timestamp: edge.timestamp || edge.block_timestamp,
                                block_height: edge.block_height,
                                token_symbol: edge.token_symbol,
                                successful: edge.successful !== false,
                                importance: edge.importance || 'normal',
                                in_path: true,
                            }
                        });
                    }
                });
            }
            console.log(`  → Adding forward trace partial results (${fwNodesAdded}/${forwardTrace.nodes.length} connected nodes, no exchange found)`);
        }

        // 1D. Enrich counterparty nodes already on canvas (emitted by backend DiscoveryEmitter).
        // Backend emits node_discovered + edge_discovered for top counterparties during
        // the counterparty_triage step — no synthetic edge creation needed here.
        // This pass only updates labels/metadata for nodes that arrived via SSE.
        const counterparties = sections.counterparty_triage.data?.counterparties || sections.counterparty_triage.data?.ranked || [];
        const topCounterparties = selectTopCounterparties(counterparties);

        if (topCounterparties.length > 0) {
            const enrichUpdates: Array<{ id: string; data: Record<string, any> }> = [];
            topCounterparties.forEach((cp: any) => {
                const rawAddr = cp.address || cp.counterparty;
                if (!rawAddr) return;
                const addr = normalizeCanvasAddress(rawAddr);
                const existingNode = cytoscapeInstance.getElementById(addr);
                if (existingNode.length) {
                    // Enrich with entity data from triage results
                    const dataUpdate: Record<string, any> = {};
                    if (cp.entity_name && cp.entity_name !== 'Unknown') {
                        existingNode.data('label', cp.entity_name);
                        existingNode.data('entity_name', cp.entity_name);
                        dataUpdate.label = cp.entity_name;
                        dataUpdate.entity_name = cp.entity_name;
                    }
                    if (cp.entity_type) {
                        existingNode.data('entity_type', cp.entity_type);
                        dataUpdate.entity_type = cp.entity_type;
                    }
                    
                    const isCritical = cp.risk_tier === 'critical' || cp.risk_tier === 'severe' || cp.risk_tier === 'high' ||
                                     cp.entity_type === 'exchange' || cp.entity_type === 'Exchange';
                    
                    applyCanvasIntent(existingNode, isCritical ? CanvasNodeIntent.CRITICAL_ENDPOINT : CanvasNodeIntent.HEAVY_CONTEXT);
                    
                    if (Object.keys(dataUpdate).length > 0) {
                        enrichUpdates.push({ id: addr, data: dataUpdate });
                    }
                }
            });
            // Persist enrichment to React elements state (survives react-cytoscapejs re-renders)
            if (enrichUpdates.length > 0 && onEnrichElements) {
                onEnrichElements(enrichUpdates);
            }
            console.log(`  → Enriched ${topCounterparties.length} top counterparties on canvas`);
        }

        // Add all new elements to the graph
        if (elementsToAdd.length > 0 && onAddToGraph) {
            console.log(`  → Adding ${elementsToAdd.length} total elements to canvas`);
            onAddToGraph(elementsToAdd);
        }

        // Step 2: Apply visual styling to canvas (Gold Path, escalation, badges)
        const results = {
            address: targetAddress,
            exchange_detection: sections.exchange_detection.data,
            velocity_profile: sections.velocity.data ? {
                classification: sections.velocity.data.velocity_profile || sections.velocity.data.classification,
            } : undefined,
            signal_assessment: sections.signal_assessment.data ? {
                recommendation: sections.signal_assessment.data.escalation_recommendation || sections.signal_assessment.data.recommendation,
                families_triggered: sections.signal_assessment.data.families_triggered || 0,
                total_families: sections.signal_assessment.data.families_tested || 8,
            } : undefined,
            backward_trace: sections.backward_trace.data,
            // P0-c: forward_trace (the cash-out / off-ramp corridor) was omitted
            // here, so canvasSync always received [] and the off-ramp path never
            // rendered/golded — gold only ever marked the inbound side. Pass it
            // through so the off-ramp corridor reaches the canvas renderer.
            forward_trace: sections.forward_trace.data,
            counterparty_triage: sections.counterparty_triage.data?.counterparties || sections.counterparty_triage.data?.ranked,
        };

        syncInvestigationToCanvas(results, cytoscapeInstance, chain || '');

        // Step 3: Canvas cleanup — see the dedicated useEffect below
        // (`discovery-class cleanup`). The cleanup intentionally lives outside
        // this once-only completion-sync block so that SSE buffer flushes that
        // land after `complete` (the buffer is on a 200ms interval) still get
        // cleaned up. Doing it here would leak any element that arrived in the
        // race window between cleanup and the next buffer flush — exactly the
        // L3 leak observed on 2026-05-05 (3 elements, including the $474k
        // Bitget edge, stuck dimmed at 0.55 26 minutes after complete).

        // Step 3b: SSE edge post-processing — consolidate multi-edge pairs.
        // During streaming, each discovered transaction emits a separate `edge_discovered` event,
        // resulting in 10-50 parallel edges between the same two nodes.
        // Now that the stream is complete, merge them into one consolidated edge per pair
        // (same behaviour as `expandNode` — label: "15 txs · 80,538 USDT").
        // Gold-path, dex-swap, p2p-link, and inferred edges are protected and left untouched.
        //
        // IMPORTANT: Run setElements whenever any edge needs consolidation, NOT only
        // when the array length shrinks. consolidateExistingEdges produces enriched
        // data (`_widthTier`, `_visibility`, `is_consolidated`, `tx_count`) on every
        // regular edge it processes — including single-tx pairs that don't collapse.
        // The old length-only check threw away that enrichment for unique-pair edges,
        // which is why the 2026-05-05 v18.1 cy dump showed backward-trace hops 2 & 3
        // (TDqSqu→TREys, TREys→THMs) sitting on the canvas with bare data and
        // missing widths/visibility. Mirror the Workbench.tsx post-complete sweep:
        // detect any unconsolidated regular edge, then setElements unconditionally.
        {
            const PROTECTED_TYPES = new Set(['gold-path', 'dex-swap', 'p2p-link', 'inferred', 'gold-path-connector']);
            const currentElements = useGraphStore.getState().elements;
            const hasUnconsolidated = currentElements.some(el =>
                el.data?.source && !el.data?.is_consolidated && !PROTECTED_TYPES.has(el.data?.edgeType)
            );
            if (hasUnconsolidated) {
                const consolidated = consolidateExistingEdges(currentElements);
                // Edge-width/visibility enrichment only — no new discovery nodes.
                // Data-layer tx enrich follows immediately in Step 4 below.
                useGraphStore.getState().setElements(consolidated); // noqa: data-layer-first
                console.log(
                    `✨ SSE edge consolidation: ${currentElements.filter(e => e.data?.source).length} → ` +
                    `${consolidated.filter((e: any) => e.data?.source).length} edges (always re-set so single-pair enrichment survives)`
                );
            }
        }

        // Step 4: Enrich expanded_transactions for all discovered nodes (data-layer first)
        // This populates Node Inspector Transfers tab for SSE-discovered nodes.
        // Uses the shared enrichExpandedTransactions utility (same pattern as Follow the Money).
        const discoveredAddrs: string[] = [];
        const els = useGraphStore.getState().elements;
        for (const el of els) {
            if (el.data?.source) continue; // skip edges
            if (!el.data?.id || el.data.id === targetAddress) continue;
            if (el.data.expanded_transactions?.length) continue;
            // Skip synthesized placeholder nodes — ghost (undisclosed subpoena
            // counterparty) and the frontend's coinbase / contract-creation /
            // OP_RETURN stand-ins. None of them are real addresses.
            if (isPlaceholderNode(el)) continue;
            discoveredAddrs.push(el.data.id);
        }
        if (discoveredAddrs.length > 0 && targetChain) {
            enrichExpandedTransactions({
                chain: targetChain,
                setElements: useGraphStore.getState().setElements,
                getElements: () => useGraphStore.getState().elements,
                addresses: discoveredAddrs,
                txLimit: 50,
                skipIfExists: true,
            }).catch(err => console.error('Post-investigation enrichment failed:', err));
        }

        console.log('✨ Canvas sync + auto-add complete');
    }, [isComplete, cytoscapeInstance, targetAddress, targetChain, sections, onAddToGraph]);

    // 🧹 DISCOVERY-CLASS CLEANUP — strip `.discovering` / `.pruned` from every
    // element on the canvas and restore `_visibility` to VISIBLE. This is in a
    // dedicated effect (not inside the once-only completion sync above) for a
    // specific race condition: SSE events buffer for 200ms before flushing onto
    // the canvas, so an event accepted just before the orchestrator's `complete`
    // can land *after* the once-only cleanup ran, leaving the element stuck
    // dimmed forever (e.g. the $474k Bitget edge in the 2026-05-05 v6 test).
    //
    // Fix: re-run cleanup on every section update once the investigation has
    // completed AND schedule a deferred sweep at 600ms (longer than the SSE
    // buffer interval) so any flush that lands after the section update still
    // gets caught.
    useEffect(() => {
        // R6 / L3 cleanup: also sweep when the run returns to idle without
        // `complete` (user cancel, hung SSE, or orchestrator abort) so nodes
        // are not left stuck at discovering/DIMMED.
        const shouldSweep = Boolean(cytoscapeInstance) && (isComplete || (!isRunning && !isComplete));
        if (!shouldSweep || !cytoscapeInstance) return;
        // Skip virgin idle (never ran) — no discovering classes expected.
        const everRan = Object.values(sections || {}).some(
            (s: any) => s && s.status && s.status !== 'idle',
        );
        if (!isComplete && !everRan) return;
        const sweep = () => {
            cytoscapeInstance.elements('.discovering').forEach((el: any) => {
                el.removeClass('discovering');
                el.data('_visibility', VISIBILITY.VISIBLE);
            });
            cytoscapeInstance.elements('.pruned').forEach((el: any) => {
                el.removeClass('pruned');
                el.data('_visibility', VISIBILITY.VISIBLE);
            });
            // Catch the merge-into-DIMMED race the discovering sweep can't
            // see: addToCanvas preserves the existing element's _visibility
            // when merging, so a gold-path edge that landed on top of a
            // DIMMED SSE edge stays at 0.55 even though its discovering class
            // has been cleared. reconcileEdgeVisibility restores trace edges
            // to VISIBLE and walks every other edge to a sensible level.
            reconcileEdgeVisibility(cytoscapeInstance);
        };
        sweep();
        const t = setTimeout(sweep, 600);
        return () => clearTimeout(t);
    }, [isComplete, isRunning, cytoscapeInstance, sections]);

    // 🔄 PROGRESSIVE VIZ: Push results to canvas as each section completes (not just at end)
    const progressiveSyncedRef = useRef<Set<string>>(new Set());

    // Reset synced tracker when investigation resets
    useEffect(() => {
        if (!isRunning && !isComplete) {
            progressiveSyncedRef.current = new Set();
        }
    }, [isRunning, isComplete]);

    useEffect(() => {
        if (!isRunning || !cytoscapeInstance || !onAddToGraph || !targetAddress) return;
        const synced = progressiveSyncedRef.current;

        // Exchange detection: update target node label if on canvas
        if (sections.exchange_detection.status === 'loaded' && !synced.has('exchange_detection')) {
            synced.add('exchange_detection');
            const exData = sections.exchange_detection.data;
            if (exData?.is_exchange && exData?.exchange_name) {
                const targetNode = cytoscapeInstance.getElementById(targetAddress);
                if (targetNode.length > 0) {
                    targetNode.data('entity_type', 'Exchange');
                    targetNode.data('entity_name', exData.exchange_name);
                    targetNode.data('label', exData.exchange_name);
                }
            }
        }

        // Counterparty triage: enrich nodes already emitted by backend DiscoveryEmitter.
        // No synthetic node/edge creation — backend emits them via SSE during the step.
        if (sections.counterparty_triage.status === 'loaded' && !synced.has('counterparty_triage')) {
            synced.add('counterparty_triage');
            const counterparties = sections.counterparty_triage.data?.counterparties || sections.counterparty_triage.data?.ranked || [];
            const topCounterparties = selectTopCounterparties(counterparties);
            if (topCounterparties.length > 0 && cytoscapeInstance) {
                topCounterparties.forEach((cp: any) => {
                    const rawAddr = cp.address || cp.counterparty;
                    if (!rawAddr) return;
                    const addr = normalizeCanvasAddress(rawAddr);
                    const node = cytoscapeInstance.getElementById(addr);
                    if (node.length) {
                        if (cp.entity_name && cp.entity_name !== 'Unknown') {
                            node.data('label', cp.entity_name);
                            node.data('entity_name', cp.entity_name);
                        }
                        if (cp.entity_type) node.data('entity_type', cp.entity_type);
                        
                        const isCritical = cp.risk_tier === 'critical' || cp.risk_tier === 'severe' || cp.risk_tier === 'high' ||
                                         cp.entity_type === 'exchange' || cp.entity_type === 'Exchange';
                                         
                        applyCanvasIntent(node, isCritical ? CanvasNodeIntent.CRITICAL_ENDPOINT : CanvasNodeIntent.HEAVY_CONTEXT);
                    }
                });
            }
        }

        // Backward trace: add path nodes/edges incrementally
        if (sections.backward_trace.status === 'loaded' && !synced.has('backward_trace')) {
            synced.add('backward_trace');
            const trace = sections.backward_trace.data;
            const newEls: any[] = [];
            if (trace?.paths && trace.paths.length > 0) {
                const primaryPath = trace.paths[0];
                const pathAddresses = (primaryPath.path || primaryPath.addresses || []).map((a: string) => normalizeCanvasAddress(a));
                const primaryEdges: any[] = primaryPath.edges || [];
                pathAddresses.forEach((addr: string, idx: number) => {
                    if (!cytoscapeInstance.getElementById(addr).length) {
                        const isSource = idx === pathAddresses.length - 1;
                        const entity = primaryPath.target_entity || primaryPath.source;
                        newEls.push({ data: {
                            id: addr,
                            label: isSource && entity?.name ? entity.name : `${addr.slice(0, 6)}...${addr.slice(-4)}`,
                            type: 'address', entity_type: isSource ? entity?.type : undefined,
                        }});
                    }
                });
                for (let i = 0; i < pathAddresses.length - 1; i++) {
                    // Money-flow direction: backward-trace path is in reverse
                    // money-flow order ([root, …, origin]). Backend's
                    // `primaryEdges[i].from`/`to` already encode the true
                    // sender→receiver direction. SSE `edge_discovered` events
                    // use the same orientation, so matching it here prevents
                    // duplicate-direction edges on canvas (see Workbench.tsx
                    // addTracePathToGraph for the canonical version of this
                    // fix).
                    const pe = primaryEdges[i];
                    const src = (pe?.from && pe?.to) ? normalizeCanvasAddress(pe.from) : pathAddresses[i + 1];
                    const tgt = (pe?.from && pe?.to) ? normalizeCanvasAddress(pe.to)   : pathAddresses[i];
                    const edgeId = `${src}-${tgt}`;
                    if (!cytoscapeInstance.getElementById(edgeId).length) {
                        newEls.push({ data: { id: edgeId, source: src, target: tgt, type: 'transaction', label: '', in_path: true }});
                    }
                }
            } else if (trace?.nodes?.length > 0) {
                // C4 FIX: Only add nodes that appear in at least one trace edge,
                // preventing orphan nodes from BFS-explored addresses.
                const bwEdgeConnected = new Set<string>();
                if (trace.edges) {
                    trace.edges.forEach((edge: any) => {
                        const s = normalizeCanvasAddress(edge.from || edge.source || edge.from_address || '');
                        const t = normalizeCanvasAddress(edge.to || edge.target || edge.to_address || '');
                        if (s && t) { bwEdgeConnected.add(s); bwEdgeConnected.add(t); }
                    });
                }
                trace.nodes.forEach((node: any) => {
                    const addr = normalizeCanvasAddress(node.id || node.address || '');
                    if (!addr || cytoscapeInstance.getElementById(addr).length) return;
                    if (!bwEdgeConnected.has(addr)) return;
                    newEls.push({ data: {
                        id: addr, label: node.entity_name || `${addr.slice(0, 6)}...${addr.slice(-4)}`,
                        type: 'address', entity_type: node.entity_type, entity_name: node.entity_name, risk_score: node.risk_score,
                    }});
                });
                if (trace.edges) {
                    trace.edges.forEach((edge: any) => {
                        const source = normalizeCanvasAddress(edge.from || edge.source || edge.from_address || '');
                        const target = normalizeCanvasAddress(edge.to || edge.target || edge.to_address || '');
                        const edgeId = edge.tx_hash || `${source}-${target}`;
                        if (source && target && !cytoscapeInstance.getElementById(edgeId).length) {
                            const eVal = edge.value || edge.amount;
                            newEls.push({ data: { id: edgeId, source, target, type: 'transaction', label: computeEdgeLabel(eVal, edge.token_symbol, edge.tx_count), tx_hash: edge.tx_hash, value: eVal, timestamp: edge.timestamp, in_path: true }});
                        }
                    });
                }
            }
            if (newEls.length > 0) onAddToGraph(newEls);
        }

        // Forward trace: add path nodes/edges incrementally
        if (sections.forward_trace.status === 'loaded' && !synced.has('forward_trace')) {
            synced.add('forward_trace');
            const trace = sections.forward_trace.data;
            const newEls: any[] = [];
            if (trace?.paths && trace.paths.length > 0) {
                const primaryPath = trace.paths[0];
                const pathAddresses = (primaryPath.path || primaryPath.addresses || []).map((a: string) => normalizeCanvasAddress(a));
                const primaryEdges: any[] = primaryPath.edges || [];
                pathAddresses.forEach((addr: string, idx: number) => {
                    if (!cytoscapeInstance.getElementById(addr).length) {
                        const isDest = idx === pathAddresses.length - 1;
                        const entity = primaryPath.target_entity;
                        newEls.push({ data: {
                            id: addr,
                            label: isDest && entity?.name ? entity.name : `${addr.slice(0, 6)}...${addr.slice(-4)}`,
                            type: 'address', entity_type: isDest ? entity?.type : undefined,
                        }});
                    }
                });
                for (let i = 0; i < pathAddresses.length - 1; i++) {
                    // Forward-trace path is already in money-flow order, so the
                    // positional pair matches what SSE emits. We still prefer
                    // the backend's `pe.from`/`to` when present so the
                    // direction stays authoritative if the path order ever
                    // changes upstream.
                    const pe = primaryEdges[i];
                    const src = (pe?.from && pe?.to) ? normalizeCanvasAddress(pe.from) : pathAddresses[i];
                    const tgt = (pe?.from && pe?.to) ? normalizeCanvasAddress(pe.to)   : pathAddresses[i + 1];
                    const edgeId = `${src}-${tgt}`;
                    if (!cytoscapeInstance.getElementById(edgeId).length) {
                        newEls.push({ data: { id: edgeId, source: src, target: tgt, type: 'transaction', label: '', in_path: true }});
                    }
                }
            } else if (trace?.nodes?.length > 0) {
                // C4 FIX: Only add edge-connected nodes (same as backward trace)
                const fwEdgeConnected = new Set<string>();
                if (trace.edges) {
                    trace.edges.forEach((edge: any) => {
                        const s = normalizeCanvasAddress(edge.from || edge.source || edge.from_address || '');
                        const t = normalizeCanvasAddress(edge.to || edge.target || edge.to_address || '');
                        if (s && t) { fwEdgeConnected.add(s); fwEdgeConnected.add(t); }
                    });
                }
                trace.nodes.forEach((node: any) => {
                    const addr = normalizeCanvasAddress(node.id || node.address || '');
                    if (!addr || cytoscapeInstance.getElementById(addr).length) return;
                    if (!fwEdgeConnected.has(addr)) return;
                    newEls.push({ data: {
                        id: addr, label: node.entity_name || `${addr.slice(0, 6)}...${addr.slice(-4)}`,
                        type: 'address', entity_type: node.entity_type, entity_name: node.entity_name, risk_score: node.risk_score,
                    }});
                });
                if (trace.edges) {
                    trace.edges.forEach((edge: any) => {
                        const source = normalizeCanvasAddress(edge.from || edge.source || edge.from_address || '');
                        const target = normalizeCanvasAddress(edge.to || edge.target || edge.to_address || '');
                        const edgeId = edge.tx_hash || `${source}-${target}`;
                        if (source && target && !cytoscapeInstance.getElementById(edgeId).length) {
                            const eVal = edge.value || edge.amount;
                            newEls.push({ data: { id: edgeId, source, target, type: 'transaction', label: computeEdgeLabel(eVal, edge.token_symbol, edge.tx_count), tx_hash: edge.tx_hash, value: eVal, timestamp: edge.timestamp, in_path: true }});
                        }
                    });
                }
            }
            if (newEls.length > 0) onAddToGraph(newEls);
        }

        // Edge reconciliation: after trace step_results, recover edges dropped during live discovery
        // due to ordering (edge arrived before target node existed on canvas)
        for (const traceKey of ['backward_trace', 'forward_trace'] as const) {
            if (sections[traceKey].status === 'loaded' && !synced.has(`${traceKey}_reconcile`)) {
                synced.add(`${traceKey}_reconcile`);
                const trace = sections[traceKey].data;
                if (trace?.edges) {
                    const recoveredEdges: any[] = [];
                    trace.edges.forEach((edge: any) => {
                        const source = normalizeCanvasAddress(edge.from || edge.source || edge.from_address || '');
                        const target = normalizeCanvasAddress(edge.to || edge.target || edge.to_address || '');
                        if (!source || !target) return;
                        const edgeId = edge.tx_hash || `${source}-${target}`;
                        // Only add if both endpoints exist but edge doesn't
                        if (cytoscapeInstance.getElementById(source).length &&
                            cytoscapeInstance.getElementById(target).length &&
                            !cytoscapeInstance.getElementById(edgeId).length) {
                            const eVal = edge.value || edge.amount;
                            recoveredEdges.push({ data: {
                                id: edgeId, source, target, type: 'transaction',
                                label: computeEdgeLabel(eVal, edge.token_symbol, edge.tx_count),
                                tx_hash: edge.tx_hash, value: eVal,
                                token_symbol: edge.token_symbol, timestamp: edge.timestamp,
                            }});
                        }
                    });
                    if (recoveredEdges.length > 0) {
                        console.log(`  → Edge reconciliation: recovered ${recoveredEdges.length} edges for ${traceKey}`);
                        onAddToGraph(recoveredEdges);
                    }
                }
                // Upgrade .discovering nodes to .bfs-explored now that step is complete.
                // v5.3: opacity flows through canvasController so the store stays
                // in sync with Cytoscape (no stealth cy.data() writes).
                const dimNodeIds: string[] = [];
                cytoscapeInstance.nodes('.discovering').forEach((node: any) => {
                    node.removeClass('discovering').addClass('bfs-explored');
                    const id = node.id();
                    if (id) dimNodeIds.push(id);
                });
                if (dimNodeIds.length > 0) canvasController.dim(dimNodeIds, VISIBILITY.DIMMED, 'sse_complete_bfs_explored');
                const undimEdgeIds: string[] = [];
                cytoscapeInstance.edges('.discovering').forEach((edge: any) => {
                    edge.removeClass('discovering');
                    const id = edge.id();
                    if (id) undimEdgeIds.push(id);
                });
                if (undimEdgeIds.length > 0) canvasController.undim(undimEdgeIds, 'sse_complete_edges');
            }
        }

        // Velocity: apply badge to target node immediately
        if (sections.velocity.status === 'loaded' && !synced.has('velocity')) {
            synced.add('velocity');
            const vData = sections.velocity.data;
            const classification = vData?.velocity_profile || vData?.classification;
            if (classification) {
                const targetNode = cytoscapeInstance.getElementById(targetAddress);
                if (targetNode.length > 0) {
                    const badges: Record<string, string> = { Relay: '⚡', Distributor: '📤', Accumulator: '📥', Dormant: '💾', Mixed: '🔀' };
                    targetNode.data('velocity_badge', badges[classification] || '🔍');
                    targetNode.data('velocity_classification', classification);
                }
            }
        }

        // Signal assessment: apply escalation styling immediately (don't wait for isComplete)
        if (sections.signal_assessment.status === 'loaded' && !synced.has('signal_assessment')) {
            synced.add('signal_assessment');
            const saData = sections.signal_assessment.data;
            const recommendation = saData?.escalation_recommendation || saData?.recommendation;
            if (recommendation) {
                const targetNode = cytoscapeInstance.getElementById(targetAddress);
                if (targetNode.length > 0) {
                    if (recommendation === 'ESCALATE') {
                        applyCanvasIntent(targetNode, CanvasNodeIntent.CRITICAL_ENDPOINT);
                        targetNode.data('escalation_level', 'ESCALATE');
                    } else if (recommendation === 'INVESTIGATE') {
                        applyCanvasIntent(targetNode, CanvasNodeIntent.CRITICAL_ENDPOINT);
                        targetNode.data('escalation_level', 'INVESTIGATE');
                    }
                }
            }
        }
    }, [
        isRunning, cytoscapeInstance, targetAddress, onAddToGraph,
        sections.exchange_detection.status, sections.counterparty_triage.status,
        sections.backward_trace.status, sections.forward_trace.status,
        sections.velocity.status, sections.signal_assessment.status,
    ]);

    // Investigation mode: auto (run all), guided (pause after signal assessment), manual (click each)
    type InvestigateMode = 'auto' | 'guided' | 'manual';
    const [mode, setMode] = useState<InvestigateMode>('auto');
    const [guidedPaused, setGuidedPaused] = useState(false);

    // Hop depth controls (Progressive Disclosure - Layer 3)
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [backwardTraceHops, setBackwardTraceHops] = useState(() => {
        const saved = localStorage.getItem(LS_KEY_BACKWARD_HOPS);
        return saved ? parseInt(saved) : DEFAULT_BACKWARD_HOPS;
    });
    const [multiHopExposureHops, setMultiHopExposureHops] = useState(() => {
        const saved = localStorage.getItem(LS_KEY_EXPOSURE_HOPS);
        return saved ? parseInt(saved) : DEFAULT_EXPOSURE_HOPS;
    });
    const [forwardTraceHops, setForwardTraceHops] = useState(() => {
        const saved = localStorage.getItem(LS_KEY_FORWARD_HOPS);
        return saved ? parseInt(saved) : DEFAULT_FORWARD_HOPS;
    });

    // Persist hop preferences to localStorage
    useEffect(() => {
        localStorage.setItem(LS_KEY_BACKWARD_HOPS, String(backwardTraceHops));
    }, [backwardTraceHops]);

    useEffect(() => {
        localStorage.setItem(LS_KEY_EXPOSURE_HOPS, String(multiHopExposureHops));
    }, [multiHopExposureHops]);

    useEffect(() => {
        localStorage.setItem(LS_KEY_FORWARD_HOPS, String(forwardTraceHops));
    }, [forwardTraceHops]);

    // Preset configurations
    const applyPreset = useCallback((preset: 'quick' | 'standard' | 'deep') => {
        const p = HOP_PRESETS[preset];
        setBackwardTraceHops(p.backward);
        setMultiHopExposureHops(p.exposure);
        setForwardTraceHops(p.forward);
    }, []);

    // Sync from props when URL navigation changes the root address.
    // If the new address differs from the address we investigated,
    // reset sections so the user sees "Run" instead of stale results.
    useEffect(() => {
        if (chain) setTargetChain(chain);
        if (address) setTargetAddress(address);
        const inv = investigation;
        const investigatedAddr = inv?.investigatedAddress;
        const isRunning = inv?.isRunning;
        if (address && investigatedAddr && address !== investigatedAddr && !isRunning) {
            inv?.reset?.();
        }
    }, [chain, address]);

    // Keyboard shortcut: I to close — only for floating (non-docked) mode.
    // Docked cards live in the Analysis tab; Workbench owns G/I globally.
    useEffect(() => {
        if (props.isDocked || !isVisible) return;
        const handler = (e: KeyboardEvent) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
            if (e.key === 'i' || e.key === 'I') {
                onClose();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [props.isDocked, isVisible, onClose]);

    const cycleSnap = useCallback(() => {
        setSnap(prev => prev === 'TL' ? 'BL' : prev === 'BL' ? 'BR' : 'TL');
    }, []);

    const handleRun = useCallback(() => {
        if (!targetChain || !targetAddress) return;
        if (dataMode === 'mock') {
            toast.info('Mock mode — Auto-Investigate is disabled. Switch to Live or Cached.', { duration: 5000 });
            return;
        }
        setTraceAdded(false);
        if (mode === 'guided') {
            // Guided mode: run only signal_assessment first, then pause
            setGuidedPaused(false);
            refresh(targetChain, targetAddress, 'signal_assessment', backwardTraceHops, multiHopExposureHops, forwardTraceHops);
        } else if (mode === 'manual') {
            // Manual mode: don't auto-run, user clicks per section
            return;
        } else {
            // Default mode: use unified SSE stream for live progress & canvas updates
            // Buffer SSE elements and flush every 200ms so nodes and their edges
            // arrive in the same onAddToGraph() call. This prevents the race
            // condition where an edge is dropped because its endpoint node's
            // React state update hasn't committed yet.
            let sseBuffer: any[] = [];
            let sseFlushTimer: ReturnType<typeof setTimeout> | null = null;
            const flushSseBuffer = () => {
                sseFlushTimer = null;
                if (sseBuffer.length > 0 && onAddToGraph) {
                    // Sort: nodes first, then edges — ensures endpoints exist
                    const batch = sseBuffer.sort((a, b) =>
                        (a.data.source ? 1 : 0) - (b.data.source ? 1 : 0)
                    );
                    sseBuffer = [];
                    onAddToGraph(batch);
                }
            };
            const enqueueSseElement = (el: any) => {
                sseBuffer.push(el);
                if (!sseFlushTimer) {
                    sseFlushTimer = setTimeout(flushSseBuffer, 200);
                }
            };

            // B2 (FRRO orphan-pile fix): hold each discovered node until an edge
            // references it, so NO node reaches the canvas without its connecting
            // edge. node_discovered -> pendingNodes; edge_discovered releases both
            // endpoints into the SAME flush (the flush sorts nodes-before-edges, so
            // the endpoint is committed before its edge and never dropped). A node
            // that no edge ever references stays suppressed — it belongs in the data
            // layer / Node Inspector (e.g. multi-hop exposure rows), not as a floating
            // orphan. See FRONTEND_STATE_ARCHITECTURE.md + CONSISTENCY_TODO B2.
            const pendingNodes = new Map<string, any>();
            const releaseNode = (id: string) => {
                const el = pendingNodes.get(id);
                if (el) { pendingNodes.delete(id); enqueueSseElement(el); }
            };

            // R24: no per-flush relayout while the stream runs. GraphCanvas holds
            // expansion layouts until a step boundary, completion or user action.
            streamLayoutHeldRef.current = true;
            usePendingLayoutStore.getState().beginStreamHold();
            runViaStream(targetChain, targetAddress, {
                backwardHops: backwardTraceHops,
                exposureHops: multiHopExposureHops,
                forwardHops: forwardTraceHops,
                caseId: caseId || null,
                investigationId: caseId ? new URLSearchParams(window.location.search).get('investigation_id') : null,
                onLiveGraphEvent: (event) => {
                    if (!onAddToGraph) return;
                    if (event.type === 'node_discovered') {
                        const d = event.data;
                        const nid = normalizeCanvasAddr(d.id);
                        // Data layer first (CANVAS_DATA_DISPLAY_SEPARATION.md §5.6).
                        // If the backend ships per-tx detail with the discovery
                        // event, write it to the per-node transaction store
                        // BEFORE the canvas element. Today's backend doesn't
                        // populate `transactions`/`tx_hashes` on node_discovered;
                        // this branch is here so the contract is honoured the
                        // moment Step 4 ships per-tx payloads.
                        if (nid && Array.isArray(d.transactions) && d.transactions.length > 0) {
                            try {
                                const txStore = useTransactionStore.getState();
                                txStore.appendNodeTransactions(
                                    nid,
                                    d.transactions.map((t: any) => toExpandedTx(t, nid)),
                                );
                            } catch (err) {
                                console.warn('[AutoInvestigate] node_discovered tx store write failed', err);
                            }
                        }
                        // B2: hold this node in the pending buffer instead of putting it
                        // straight on the canvas. It is released only when an edge
                        // references it (see releaseNode in edge_discovered).
                        if (nid) {
                            pendingNodes.set(nid, {
                                data: {
                                    id: nid,
                                    label: d.label || d.entity_name || (nid.slice(0, 10) + '...'),
                                    type: 'address',
                                    entity_type: normalizeEntityType(d.entity_type, d.entity_name || d.label),
                                    entity_name: d.entity_name,
                                    risk_score: d.risk_score,
                                    chain: d.chain,
                                    _visibility: VISIBILITY.DIMMED, // discovering = DIMMED
                                    // CANVAS_PLACEMENT_LANGUAGE §3.3: spatial-role channel.
                                    // Backend declares; layout owns x/y. Infer from step when absent.
                                    placement_role: d.placement_role || inferRoleFromStep(d.step || d.discovered_via),
                                    ...(d.step ? { step: d.step } : {}),
                                    ...(d.discovered_via ? { discovered_via: d.discovered_via } : {}),
                                },
                                classes: 'discovering',
                            });
                        }
                    } else if (event.type === 'edge_discovered') {
                        const d = event.data;
                        const nsrc = normalizeCanvasAddr(d.source);
                        const ntgt = normalizeCanvasAddr(d.target);
                        // Data layer first. When the backend SSE payload
                        // includes per-tx detail, write to the Inspector's
                        // per-node tx store under BOTH endpoints so a click
                        // on either node — or on the edge itself — surfaces
                        // the real underlying transactions instead of the
                        // aggregate count alone.
                        //
                        // Two payload shapes accepted:
                        //   (a) `transactions: [{tx_hash, value, ...}, ...]`
                        //       — preferred for aggregated edges
                        //       (counterparty_triage emits this since the
                        //       May 5 2026 restoration). Each record becomes
                        //       a row the Inspector can render.
                        //   (b) Single-tx event: just `tx_hash` + value +
                        //       timestamp + token_symbol on the event itself.
                        //       BFS trace (graph_service.trace_address) and
                        //       the gold-path emitters use this — one event
                        //       per underlying tx. Synthesise a one-row tx
                        //       from the event fields and store it the same
                        //       way; this is data the backend DID send,
                        //       not fabricated edge metadata.
                        if (nsrc && ntgt) {
                            const txsToStore: any[] = [];
                            if (Array.isArray(d.transactions) && d.transactions.length > 0) {
                                for (const t of d.transactions) txsToStore.push(t);
                            } else if (d.tx_hash) {
                                txsToStore.push({
                                    tx_hash: d.tx_hash,
                                    from_address: nsrc,
                                    to_address: ntgt,
                                    value: d.value ?? 0,
                                    timestamp: d.timestamp ?? null,
                                    token_symbol: d.token_symbol ?? null,
                                });
                            }
                            if (txsToStore.length > 0) {
                                try {
                                    const txStore = useTransactionStore.getState();
                                    txStore.appendNodeTransactions(nsrc, txsToStore.map((t: any) => toExpandedTx(t, nsrc)));
                                    txStore.appendNodeTransactions(ntgt, txsToStore.map((t: any) => toExpandedTx(t, ntgt)));
                                } catch (err) {
                                    console.warn('[AutoInvestigate] edge_discovered tx store write failed', err);
                                }
                            }
                        }
                        // Build label using shared format: "15 txs · 1,234 USDT"
                        let edgeLabel = '';
                        if (d.value != null && d.value !== 0) {
                            const tokMap = new Map<string, number>();
                            const tok = safeStr(d.token_symbol) || '';
                            if (tok) tokMap.set(tok, Number(d.value) || 0);
                            edgeLabel = formatConsolidatedLabel(d.tx_count || 1, Number(d.value) || 0, tokMap, d.timestamp ?? d.last_timestamp ?? null);
                        } else if (d.tx_count && d.tx_count > 1) {
                            edgeLabel = `${d.tx_count} txs`;
                        }
                        const edgeStyle = resolveEdgeStyleKey(d.edge_type);
                        logEdge({
                            path: 'sse',
                            edgeId: `${nsrc}-${ntgt}-${d.tx_hash || 'live'}`,
                            source: nsrc,
                            target: ntgt,
                            direction: d.direction || 'unknown',
                            value: d.value ?? 0,
                            tokenSymbol: d.token_symbol,
                            edgeType: edgeStyle.edgeType,
                            txCount: d.tx_count,
                            isConsolidated: !!(d.tx_count && d.tx_count > 1),
                            reason: `step=${d.step || '?'}`,
                        });
                        // B2: release both endpoints from the pending buffer FIRST, so
                        // each node is committed in the same flush as (and sorted before)
                        // its edge — never orphaned, never dropped.
                        releaseNode(nsrc);
                        releaseNode(ntgt);
                        enqueueSseElement({
                            data: {
                                id: `${nsrc}-${ntgt}-${d.tx_hash || 'live'}`,
                                source: nsrc,
                                target: ntgt,
                                value: d.value,
                                type: edgeStyle.edgeType,
                                edgeType: edgeStyle.edgeType,
                                // The backend's declared meaning, kept for the
                                // Inspector and rules keyed on it (counterfeit).
                                edge_type: d.edge_type || undefined,
                                ...(edgeStyle.data || {}),
                                label: edgeLabel,
                                tx_count: d.tx_count,
                                token_symbol: d.token_symbol,
                                tx_hash: d.tx_hash || null,
                                timestamp: d.timestamp || null,
                                is_consolidated: (d.tx_count && d.tx_count > 1) ? true : undefined,
                                // L2 fix (May 5): use the documented DIMMED preset
                                // (0.55) instead of an undocumented 0.3. The split
                                // 'edges dim more than nodes' was an unfounded
                                // tweak — both should follow the single DIMMED
                                // semantic per constants/visibility.ts and R11.
                                _visibility: VISIBILITY.DIMMED,
                            },
                            classes: 'discovering',
                        });
                    } else if (event.type === 'decision' && event.data?.action === 'pruned') {
                        // Mark pruned nodes on canvas
                        const cy = cytoscapeInstance;
                        if (cy) {
                            const addr = normalizeCanvasAddress(event.data.address);
                            const node = cy.getElementById(addr);
                            if (node.length) {
                                node.removeClass('discovering').addClass('pruned');
                                canvasController.dim([addr], VISIBILITY.PRUNED, 'sse_decision_pruned');
                            }
                        }
                    }
                },
            });
        }
    }, [targetChain, targetAddress, run, runViaStream, refresh, onAddToGraph, mode, backwardTraceHops, multiHopExposureHops, forwardTraceHops, dataMode, caseId]);

    // Per-node investigate: after sticky target catches up, kick off handleRun once.
    // Right-click may set targetAddress before the selectedNode prop syncs — allow
    // that path when props are still catching up (address unset or already matching).
    useEffect(() => {
        if (!pendingInvestigateSelectedRef.current) return;
        if (!targetAddress || !targetChain) return;
        if (address && address !== targetAddress) return;
        if (chain && chain !== targetChain) return;
        if (isRunning) return;
        pendingInvestigateSelectedRef.current = false;
        handleRun();
    }, [address, chain, targetAddress, targetChain, isRunning, handleRun]);

    const handleGuidedContinue = useCallback(() => {
        if (!targetChain || !targetAddress) return;
        setGuidedPaused(false);
        // Run remaining sections after signal_assessment
        const remaining: InvestigationKey[] = ['velocity', 'counterparty_triage', 'backward_trace', 'multi_hop_exposure', 'adaptive_transactions', 'exchange_detection'];
        for (const key of remaining) {
            if (sections[key].status === 'idle') {
                refresh(targetChain, targetAddress, key, backwardTraceHops, multiHopExposureHops, forwardTraceHops);
            }
        }
    }, [targetChain, targetAddress, sections, refresh, backwardTraceHops, multiHopExposureHops, forwardTraceHops]);

    // Guided mode: detect when signal_assessment finishes and pause
    useEffect(() => {
        if (mode !== 'guided') return;
        const sa = sections.signal_assessment;
        if (sa.status === 'loaded' || sa.status === 'error') {
            const restAllIdle = ['velocity', 'counterparty_triage', 'backward_trace', 'multi_hop_exposure', 'adaptive_transactions', 'exchange_detection']
                .every(k => sections[k as InvestigationKey].status === 'idle');
            if (restAllIdle) setGuidedPaused(true);
        }
    }, [mode, sections]);

    const handleRefresh = useCallback((key: InvestigationKey) => {
        if (!targetChain || !targetAddress) return;
        refresh(targetChain, targetAddress, key, backwardTraceHops, multiHopExposureHops, forwardTraceHops);
    }, [targetChain, targetAddress, refresh, backwardTraceHops, multiHopExposureHops, forwardTraceHops]);

    const handleEditApply = useCallback(() => {
        const trimmed = editAddress.trim();
        if (!trimmed) return;
        setTargetChain(editChain);
        setTargetAddress(trimmed);
        setIsEditing(false);
        setTraceAdded(false);
        reset();
    }, [editChain, editAddress, reset]);

    const handleEditCancel = useCallback(() => {
        setEditChain(targetChain);
        setEditAddress(targetAddress);
        setIsEditing(false);
    }, [targetChain, targetAddress]);

    const handleEditStart = useCallback(() => {
        setEditChain(targetChain);
        setEditAddress(targetAddress);
        setIsEditing(true);
    }, [targetChain, targetAddress]);

    const handleAddTraceToGraph = useCallback(() => {
        const traceData = sections.backward_trace?.data;
        if (!traceData || !onAddToGraph) return;
        const els = convertTraceToElements(traceData);
        if (els.length > 0) {
            onAddToGraph(els);
            setTraceAdded(true);
        }
    }, [sections.backward_trace, onAddToGraph]);

    const handleAddForwardTraceToGraph = useCallback(() => {
        const fwdData = sections.forward_trace?.data;
        if (!fwdData || !onAddToGraph) return;
        const els = convertTraceToElements(fwdData);
        if (els.length > 0) {
            onAddToGraph(els);
            setForwardTraceAdded(true);
        }
    }, [sections.forward_trace, onAddToGraph]);

    const handleAddCounterpartiesToGraph = useCallback(() => {
        if (!onAddToGraph || !cytoscapeInstance || !targetAddress) return;
        const counterparties = sections.counterparty_triage.data?.counterparties || sections.counterparty_triage.data?.ranked || [];
        const topCPs = selectTopCounterparties(counterparties);
        if (topCPs.length === 0) return;

        const newEls: any[] = [];
        topCPs.forEach((cp: any) => {
            const addr = cp.address || cp.counterparty;
            if (addr && !cytoscapeInstance.getElementById(addr).length) {
                newEls.push({ data: {
                    id: addr,
                    label: cp.entity_name || `${addr.slice(0, 6)}...${addr.slice(-4)}`,
                    type: 'address', entity_type: cp.entity_type, entity_name: cp.entity_name,
                    risk_score: cp.risk_score,
                }});
            }
            if (addr) {
                const edgeId = `${targetAddress}-${addr}-cp`;
                if (!cytoscapeInstance.getElementById(edgeId).length) {
                    const primaryVol = Number(cp.primary_volume) || 0;
                    const primaryTok = cp.primary_token || '';
                    const volume = primaryVol > 0 ? primaryVol : ((cp.volume_in || cp.total_sent || 0) + (cp.volume_out || cp.total_received || 0));
                    newEls.push({ data: {
                        id: edgeId, source: targetAddress, target: addr,
                        type: 'transaction',
                        label: volume > 0 ? `${volume.toFixed(2)}${primaryTok ? ` ${primaryTok}` : ''}` : '',
                        value: volume || undefined,
                    }});
                }
            }
        });
        if (newEls.length > 0) {
            onAddToGraph(newEls);
            setCounterpartiesAdded(true);
        }
    }, [sections.counterparty_triage, onAddToGraph, cytoscapeInstance, targetAddress]);

    // Filter canvas edges by importance
    const filterCanvasByImportance = useCallback((threshold: number) => {
        if (!cytoscapeInstance) return;

        cytoscapeInstance.edges().forEach((edge: any) => {
            const importance = edge.data('importance') || 0.5;

            // Calculate opacity based on importance
            let opacity: number;
            if (importance >= 0.8) {
                opacity = 1.0; // Critical: always visible
            } else if (importance >= 0.5) {
                opacity = 0.7; // Important: clearly visible
            } else if (importance >= threshold) {
                opacity = 0.4; // Above threshold: dimmed
            } else {
                opacity = 0.1; // Below threshold: nearly invisible
            }

            // Update edge styling
            edge.style({
                opacity: opacity,
                'line-style': importance >= 0.5 ? 'solid' : 'dotted',
                'width': importance >= 0.8 ? 3 : (importance >= 0.5 ? 2 : 1),
            });
        });
    }, [cytoscapeInstance]);

    // Apply importance filter when it changes or canvas updates
    useEffect(() => {
        if (cytoscapeInstance && isComplete) {
            filterCanvasByImportance(importanceFilter);
        }
    }, [cytoscapeInstance, importanceFilter, isComplete, filterCanvasByImportance]);

    const handleViewLeReport = useCallback(async () => {
        const degradedSections = (Object.keys(sections) as InvestigationKey[])
            .filter(k => sections[k].health === 'degraded');
        const erroredSections = (Object.keys(sections) as InvestigationKey[])
            .filter(k => sections[k].status === 'error');

        // Only downgrade to limited summary when CRITICAL sections fail entirely.
        // Core sections: velocity, counterparty_triage, backward_trace, signal_assessment.
        // Non-critical (supplementary): multi_hop_exposure, adaptive_transactions, forward_trace.
        // Partial multi_hop failures (e.g. 10/50 sub-analyses failed) should NOT
        // prevent a full report — the data is still useful at 80% coverage.
        const CRITICAL_SECTIONS: InvestigationKey[] = [
            'velocity', 'counterparty_triage', 'backward_trace', 'signal_assessment',
        ];
        const criticalErrors = erroredSections.filter(k => CRITICAL_SECTIONS.includes(k));
        const hasQualityIssues = criticalErrors.length > 0;

        const entityCount = sections.exchange_detection?.status === 'loaded'
            ? sections.exchange_detection.data?.entity_count
            : undefined;
        const html = hasQualityIssues
            ? generateOnePageSummaryHtml(
                targetChain,
                targetAddress,
                sections,
                {
                    ...triageData,
                    summary: {
                        ...(triageData?.summary || {}),
                        risk_level: `${triageData?.summary?.risk_level || 'UNKNOWN'} (DEGRADED EVIDENCE)`,
                    },
                },
            )
            : generateInvestigationReportHtml(targetChain, targetAddress, sections, triageData, dataMode, entityCount, circulation, assetRecovery);

        if (onPersistReport) {
            try {
                setIsSavingReport(true);
                await onPersistReport({
                    title: buildReportTitle(REPORT_TYPES.autoInvestigate, {
                        chain: targetChain,
                        address: targetAddress,
                    }),
                    htmlContent: html,
                    metadata: {
                        chain: targetChain,
                        address: targetAddress,
                        generated_at: new Date().toISOString(),
                        data_mode: dataMode,
                        report_mode: hasQualityIssues ? 'limited' : 'full',
                        degraded_sections: degradedSections,
                        error_sections: erroredSections,
                        sections_loaded: (Object.keys(sections) as InvestigationKey[]).filter(
                            k => sections[k].status === 'loaded'
                        ),
                        archive_source: 'view_le_report',
                    },
                });
            } catch (e) {
                // Keep UX resilient: open report even if storage fails.
                console.error('Failed to persist report artifact', e);
            } finally {
                setIsSavingReport(false);
            }
        }

        // C5/F4: mark the investigated node so the canvas badge + Stash-Tray
        // "Reported" group reflect that a report was generated for it.
        if (targetAddress) useGraphStore.getState().markNodeReported(targetAddress, 'auto_investigate');
        openReportHtmlInNewTab(html);
    }, [targetChain, targetAddress, sections, triageData, onPersistReport, dataMode, circulation, assetRecovery]);

    const degradedSectionKeys = useMemo(
        () => (Object.keys(sections) as InvestigationKey[]).filter(k => sections[k].health === 'degraded'),
        [sections],
    );
    const erroredSectionKeys = useMemo(
        () => (Object.keys(sections) as InvestigationKey[]).filter(k => sections[k].status === 'error'),
        [sections],
    );
    // Only flag quality issues for CRITICAL section errors (velocity, counterparty_triage,
    // backward_trace, signal_assessment). Partial multi_hop failures or degraded
    // supplementary sections should not prevent a full LE report.
    const CRITICAL_REPORT_SECTIONS: InvestigationKey[] = [
        'velocity', 'counterparty_triage', 'backward_trace', 'signal_assessment',
    ];
    const hasQualityIssues = erroredSectionKeys.some(k => CRITICAL_REPORT_SECTIONS.includes(k));

    // Derive escalation from signal_assessment data
    const escalation = useMemo(() => {
        const sa = sections.signal_assessment;
        if (sa.status !== 'loaded' || !sa.data) return null;
        return sa.data.escalation_recommendation || sa.data.signal_families
            ? (sa.data.escalation_recommendation || null)
            : null;
    }, [sections.signal_assessment]);

    // 📝 VERDICT SUMMARY: One-line synthesis of key findings
    const verdictSummary = useMemo(() => {
        if (!isComplete) return null;

        const parts: string[] = [];

        // 1. Exchange detection
        const exchange = sections.exchange_detection?.data;
        if (exchange?.is_exchange) {
            parts.push(`${exchange.exchange_name || 'Exchange'} wallet`);
        }

        // 2. Velocity classification
        const velocity = sections.velocity?.data;
        if (velocity?.classification || velocity?.velocity_profile) {
            const classification = velocity.classification || velocity.velocity_profile;
            parts.push(classification.toLowerCase());
        }

        // 3. Backward trace result
        const trace = sections.backward_trace?.data;
        if (trace?.found && trace.paths && trace.paths.length > 0) {
            const primaryPath = trace.paths[0];
            const targetName = primaryPath.target_entity?.name || 'exchange';
            parts.push(`${primaryPath.hops}-hop path to ${targetName}`);
        } else if (trace && !trace.found) {
            parts.push('no exchange path found');
        }

        // 4. High-risk counterparty count
        const counterparties = sections.counterparty_triage?.data?.counterparties || sections.counterparty_triage?.data?.ranked;
        if (counterparties && Array.isArray(counterparties)) {
            const highRiskCount = counterparties.filter((cp: any) => ['high', 'severe', 'critical'].includes(cp.risk_tier)).length;
            if (highRiskCount > 0) {
                parts.push(`${highRiskCount} high-risk counterparties`);
            }
        }

        // Fallback if no key findings
        if (parts.length === 0) {
            return 'Investigation complete — review findings below';
        }

        // Join with bullets for clean separation
        return parts.join(' • ');
    }, [isComplete, sections]);

    // Executive summary — generated from buildExecutiveSummary when complete
    const executiveSummary = useMemo(() => {
        if (!isComplete) return null;
        const sa = sections.signal_assessment;
        const vel = sections.velocity?.data;
        const familiesTriggered = sa.data?.families_triggered ?? null;
        const familiesTested = sa.data?.families_tested ?? null;
        const velocityProfile = vel?.velocity_profile || vel?.classification || null;
        const velocityRisk = vel?.risk_score ?? null;
        const esc = sa.data?.escalation_recommendation || null;
        const summary = buildExecutiveSummary(
            targetChain, targetAddress, triageData, esc,
            familiesTriggered, familiesTested, velocityProfile, velocityRisk, sections,
        );
        return summary || null;
    }, [isComplete, targetChain, targetAddress, triageData, sections]);

    // R-AI-21: Suggested next steps based on investigation findings
    const suggestedNextSteps = useMemo(() => {
        if (!isComplete) return null;
        const steps: { text: string; priority: 'high' | 'medium' | 'low' }[] = [];

        // Sanctions hit → escalate immediately
        if (triageData?.summary?.sanctioned) {
            steps.push({ text: 'Escalate to FEMA/ED — address is OFAC sanctioned', priority: 'high' });
        }

        // Exchange found → send Section 91 notice
        const exchange = sections.exchange_detection?.data;
        if (exchange?.is_exchange) {
            steps.push({ text: `Send Section 91 CrPC notice to ${exchange.exchange_name || 'exchange'} for KYC records`, priority: 'high' });
        }

        // Mixer exposure detected
        const exposureData = sections.multi_hop_exposure?.data;
        const hasMixerExposure = exposureData?.sources?.some?.((s: any) =>
            (s.entity_type || s.type || '').toLowerCase().includes('mixer')
        );
        if (hasMixerExposure) {
            steps.push({ text: 'Flag mixer interaction — possible fund obfuscation detected', priority: 'high' });
        }

        // High-risk counterparties
        const counterparties = sections.counterparty_triage?.data?.counterparties || sections.counterparty_triage?.data?.ranked;
        if (counterparties && Array.isArray(counterparties)) {
            const highRiskCount = counterparties.filter((cp: any) => ['high', 'severe', 'critical'].includes(cp.risk_tier)).length;
            if (highRiskCount > 0) {
                steps.push({ text: `Investigate ${highRiskCount} high-risk counterpart${highRiskCount > 1 ? 'ies' : 'y'}`, priority: 'medium' });
            }
        }

        // Backward trace found exchange path
        const trace = sections.backward_trace?.data;
        if (trace?.found && trace.paths?.length > 0) {
            const path = trace.paths[0];
            const target = path.target_entity?.name || 'identified entity';
            steps.push({ text: `Review ${path.hops || path.hop_details?.length || '?'}-hop backward trace path to ${target}`, priority: 'medium' });
        }

        // Forward trace found destination path
        const fwdTrace = sections.forward_trace?.data;
        if (fwdTrace?.found && fwdTrace.paths?.length > 0) {
            for (const fwdPath of fwdTrace.paths.slice(0, 3)) {
                const fwdTarget = fwdPath.target_entity?.name || 'destination';
                const fwdType = (fwdPath.target_entity?.type || '').toLowerCase();
                if (fwdType === 'scam' || fwdType === 'phishing' || /scam|fraud|hack/i.test(fwdTarget)) {
                    steps.push({ text: `⚠ Forward trace leads to scam-reported address (${fwdTarget})`, priority: 'high' });
                } else {
                    steps.push({ text: `Forward trace: ${fwdPath.hops || fwdPath.hop_details?.length || '?'}-hop path to ${fwdTarget}`, priority: 'medium' });
                }
            }
        }

        // Velocity profile suggests relay/distributor behavior
        const velocity = sections.velocity?.data;
        const vProfile = velocity?.velocity_profile || velocity?.classification;
        if (vProfile === 'Relay' || vProfile === 'Distributor') {
            steps.push({ text: `Address shows ${vProfile} behavior — possible mule or layering node`, priority: 'medium' });
        }

        // Default: monitor
        if (steps.length === 0) {
            steps.push({ text: 'Add address to watchlist for ongoing monitoring', priority: 'low' });
        }

        return steps;
    }, [isComplete, sections, triageData]);

    const counterpartyCount = useMemo(() => {
        const cps = sections.counterparty_triage.data?.counterparties || sections.counterparty_triage.data?.ranked || [];
        return selectTopCounterparties(cps).length;
    }, [sections.counterparty_triage.data]);

    if (!isVisible || !investigation) return null;

    const position = SNAP_POSITIONS[snap];
    const truncatedAddr = targetAddress ? `${targetAddress.slice(0, 8)}...${targetAddress.slice(-4)}` : '';
    const allIdle = Object.values(sections).every((s: any) => s.status === 'idle');
    const traceData = sections.backward_trace?.data;
    const traceNodeCount = (traceData?.nodes || []).length;
    const traceEdgeCount = (traceData?.edges || []).length;
    const fwdTraceData = sections.forward_trace?.data;
    const fwdTraceNodeCount = (fwdTraceData?.nodes || []).length;
    const fwdTraceEdgeCount = (fwdTraceData?.edges || []).length;

    return (
        <div
            ref={cardRef}
            className={clsx(
                "flex flex-col transition-all duration-200",
                props.isDocked
                    ? "relative w-full"
                    : "absolute z-20 bg-card/95 backdrop-blur border border-border rounded-xl shadow-overlay " + (isMinimized ? "w-auto" : "w-[520px] max-h-[85vh]")
            )}
            style={props.isDocked ? undefined : { ...position, position: 'absolute' }}
            data-testid="wb-auto-investigate-card"
        >
            {/* Header */}
            {!props.isDocked && (
            <div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
                <div className="flex items-center gap-2">
                    <button
                        onClick={cycleSnap}
                        className="cursor-move p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground"
                        title={`Position: ${snap}. Click to move.`}
                    >
                        <GripVertical className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                        <Wand2 className="w-4 h-4 text-emerald-500" />
                        {!isMinimized && (
                            <>
                                <span>Auto-Investigate</span>
                                <select
                                    value={mode}
                                    onChange={e => { setMode(e.target.value as typeof mode); setGuidedPaused(false); }}
                                    className="text-[10px] px-1 py-0.5 rounded bg-secondary border border-border text-muted-foreground font-medium ml-1"
                                    title="Investigation mode"
                                    disabled={isRunning}
                                >
                                    <option value="auto">Auto</option>
                                    <option value="guided">Guided</option>
                                    <option value="manual">Manual</option>
                                </select>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {isRunning && !isMinimized && (
                        <span className="text-xs text-muted-foreground mr-1">
                            {progress.completed}/{progress.total}
                        </span>
                    )}
                    {isComplete && cytoscapeInstance && !isMinimized && (
                        <button
                            onClick={() => {
                                clearInvestigationHighlighting(cytoscapeInstance);
                                if (onApplyFilterPreset) {
                                    onApplyFilterPreset({ showOnlyHighRisk: false, hideExchanges: false });
                                }
                            }}
                            className="p-1 hover:bg-accent rounded text-muted-foreground hover:text-foreground"
                            title="Clear canvas highlighting (Gold Path, dimming, etc.)"
                        >
                            <XCircle className="w-3.5 h-3.5" />
                        </button>
                    )}
                    <button
                        onClick={() => setIsMinimized(!isMinimized)}
                        className="p-1 hover:bg-accent rounded text-muted-foreground hover:text-foreground"
                        title={isMinimized ? "Expand" : "Minimize"}
                    >
                        <Minimize2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-accent rounded text-muted-foreground hover:text-foreground"
                        title="Close (I)"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
            )}

            {/* Body — docked mode grows naturally (inspector body scrolls);
                floating mode keeps its own bounded scrollbar. */}
            {(!isMinimized || props.isDocked) && (
                <><div className={clsx(
                    props.isDocked
                        ? 'w-full'
                        : 'flex-1 min-h-0 overflow-y-auto styled-scrollbar',
                )}>
                    <RestoredResultsBanner restored={investigation?.restoredFromSession ?? null} />
                    {/* Issue B — node mismatch banner.  Surfaces when the user
                        selects a different canvas node while an investigation
                        for another address is in progress or complete.  We
                        keep the existing results visible (don't lose user's
                        work) but make it explicit + offer a one-click switch. */}
                    {nodeMismatch && address && (
                        <div className="mx-3 mt-2 mb-1 p-2 rounded-lg border border-amber-500/40 bg-amber-500/10 text-[11px]">
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-amber-700 dark:text-amber-300 font-semibold mb-0.5">
                                        Showing results for a different node
                                    </div>
                                    <div className="text-muted-foreground leading-snug">
                                        These results are for{' '}
                                        <code className="font-mono text-foreground">
                                            {targetAddress.length > 16 ? `${targetAddress.slice(0, 8)}…${targetAddress.slice(-4)}` : targetAddress}
                                        </code>
                                        . You currently have{' '}
                                        <code className="font-mono text-foreground">
                                            {address.length > 16 ? `${address.slice(0, 8)}…${address.slice(-4)}` : address}
                                        </code>{' '}
                                        selected on the canvas.
                                    </div>
                                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                                        <button
                                            onClick={() => handleSwitchToSelectedNode()}
                                            disabled={isRunning}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-600 hover:bg-amber-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            title={isRunning ? 'Cancel the running investigation first' : 'Discard current results and target the selected node'}
                                        >
                                            Switch to selected node
                                        </button>
                                        {!isRunning && (
                                            <button
                                                onClick={handleInvestigateSelectedNode}
                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-primary hover:bg-primary/90 text-primary-foreground transition-colors"
                                                title="Discard current results, target the selected node, and start Auto-Investigate"
                                            >
                                                Investigate selected node
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Target address — editable */}
                    {targetAddress && !isEditing && (
                        <div className="px-3 py-1.5 border-b border-border/30 flex items-center gap-2">
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground border border-border uppercase font-bold shrink-0">
                                {targetChain}
                            </span>
                            <span className="font-mono text-sm text-foreground truncate flex-1" title={targetAddress}>
                                {truncatedAddr}
                            </span>
                            <button
                                onClick={handleEditStart}
                                className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground shrink-0"
                                title="Change target address"
                                disabled={isRunning}
                            >
                                <Pencil className="w-3 h-3" />
                            </button>
                        </div>
                    )}

                    {/* 🎯 ESCALATION BANNER — AT TOP for immediate verdict */}
                    {escalation && isComplete && !isRunning && (
                        <div className="mx-3 mt-2 mb-3">
                            <div className={clsx(
                                'flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wide',
                                escalation === 'ESCALATE' ? 'bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/30' :
                                    escalation === 'INVESTIGATE' ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30' :
                                        escalation === 'INSUFFICIENT_DATA' ? 'bg-slate-500/10 text-muted-foreground dark:text-muted-foreground border border-slate-500/30' :
                                            'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30'
                            )}>
                                {escalation === 'ESCALATE' && <AlertCircle className="w-4 h-4" />}
                                {escalation === 'INVESTIGATE' && <Eye className="w-4 h-4" />}
                                {escalation === 'INSUFFICIENT_DATA' && <AlertCircle className="w-4 h-4" />}
                                {escalation !== 'ESCALATE' && escalation !== 'INVESTIGATE' && escalation !== 'INSUFFICIENT_DATA' && <CheckCircle2 className="w-4 h-4" />}
                                Recommendation: {escalation?.replace(/_/g, ' ')}
                            </div>
                        </div>
                    )}

                    {/* 📝 VERDICT SUMMARY — One-line synthesis of findings */}
                    {verdictSummary && isComplete && !isRunning && (
                        <div className="mx-3 mb-3 -mt-1 space-y-3">
                            <p className="text-sm text-muted-foreground leading-relaxed text-center">
                                {verdictSummary}
                            </p>
                        </div>
                    )}

                    {/* 📋 EXECUTIVE SUMMARY — Collapsible narrative */}
                    {executiveSummary && isComplete && !isRunning && (
                        <div className="mx-3 mb-3">
                            <button
                                onClick={() => setExecSummaryOpen(!execSummaryOpen)}
                                className="w-full flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                            >
                                {execSummaryOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                <span>Executive Summary</span>
                                <span className="ml-auto px-1.5 py-0.5 text-[10px] bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/20 rounded font-medium">AI-GENERATED</span>
                            </button>
                            {execSummaryOpen && (
                                <p
                                    className="mt-1.5 text-sm text-foreground whitespace-pre-wrap leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: executiveSummary }}
                                />
                            )}
                        </div>
                    )}

                    {/* 🎬 PRIMARY ACTION BUTTON — Context-aware based on escalation */}
                    {isComplete && !isRunning && escalation && (
                        <div className="mx-3 mb-3">
                            {escalation === 'ESCALATE' && onNotice && (
                                <button
                                    onClick={onNotice}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-foreground font-semibold text-sm transition-colors shadow-sm"
                                >
                                    <FileText className="w-4 h-4" />
                                    Generate Enforcement Notice
                                </button>
                            )}
                            {escalation === 'INVESTIGATE' && onWatchlist && (
                                <button
                                    onClick={onWatchlist}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-foreground font-semibold text-sm transition-colors shadow-sm"
                                >
                                    <Eye className="w-4 h-4" />
                                    Add to Watchlist
                                </button>
                            )}
                            {escalation !== 'ESCALATE' && escalation !== 'INVESTIGATE' && (
                                <button
                                    onClick={() => {
                                        const ec = sections.exchange_detection?.status === 'loaded'
                                            ? sections.exchange_detection.data?.entity_count
                                            : undefined;
                                        const html = generateInvestigationReportHtml(
                                            targetChain,
                                            targetAddress,
                                            sections,
                                            triageData,
                                            dataMode,
                                            ec,
                                            circulation,
                                            assetRecovery,
                                        );
                                        // C5/F4: mark the node as reported.
                                        if (targetAddress) useGraphStore.getState().markNodeReported(targetAddress, 'auto_investigate');
                                        openReportHtmlInNewTab(html);
                                    }}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-secondary hover:bg-secondary/80 text-foreground font-semibold text-sm transition-colors border border-border"
                                >
                                    <FileText className="w-4 h-4" />
                                    Export Investigation Report
                                </button>
                            )}

                            {/* Importance Filter Slider */}
                            {isComplete && cytoscapeInstance && (
                                <div className="mt-3 pt-3 border-t border-border/30">
                                    <div className="flex items-center gap-3">
                                        <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                                            Graph Filter:
                                        </label>
                                        <div className="flex-1 flex items-center gap-2">
                                            <span className="text-xs text-muted-foreground">Low</span>
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={importanceFilter * 100}
                                                onChange={(e) => {
                                                    const threshold = parseInt(e.target.value) / 100;
                                                    setImportanceFilter(threshold);
                                                }}
                                                className="flex-1 h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
                                                title={`Show transactions with importance ≥ ${Math.round(importanceFilter * 100)}%`}
                                            />
                                            <span className="text-xs text-muted-foreground">High</span>
                                        </div>
                                        <span className="text-xs font-mono text-foreground min-w-[3ch]">
                                            {Math.round(importanceFilter * 100)}%
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {importanceFilter <= 0.2 ? '📊 Showing all transactions' :
                                         importanceFilter <= 0.5 ? '🔍 Filtering out noise' :
                                         '⭐ Showing important paths only'}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {isEditing && (
                        <div className="px-3 py-2 border-b border-border/30 space-y-1.5">
                            <div className="flex items-center gap-1.5">
                                <select
                                    value={editChain}
                                    onChange={e => setEditChain(e.target.value)}
                                    className="text-xs px-1.5 py-1 rounded bg-secondary border border-border text-foreground uppercase font-bold w-16"
                                >
                                    {SUPPORTED_CHAINS.map(c => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    value={editAddress}
                                    onChange={e => setEditAddress(e.target.value)}
                                    placeholder="Paste address..."
                                    className="flex-1 text-sm font-mono px-2 py-1 rounded bg-secondary border border-border text-foreground placeholder:text-muted-foreground min-w-0"
                                    autoFocus
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') handleEditApply();
                                        if (e.key === 'Escape') handleEditCancel();
                                    }}
                                />
                            </div>
                            <div className="flex justify-end gap-1.5">
                                <button
                                    onClick={handleEditCancel}
                                    className="px-2 py-0.5 rounded text-xs font-medium bg-secondary hover:bg-secondary/80 text-muted-foreground border border-border"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleEditApply}
                                    disabled={!editAddress.trim()}
                                    className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-foreground disabled:opacity-50"
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    )}
                    {!targetAddress && !isEditing && (
                        <div className="px-3 py-1.5 border-b border-border/30">
                            <button
                                onClick={handleEditStart}
                                className="text-sm text-muted-foreground hover:text-foreground underline"
                            >
                                Set target address...
                            </button>
                        </div>
                    )}

                    {/* Advanced Settings - Investigation Depth Controls (Progressive Disclosure - Layer 3) */}
                    {targetAddress && !isEditing && (
                        <div className="border-b border-border/30">
                            <button
                                onClick={() => setShowAdvanced(!showAdvanced)}
                                disabled={isRunning}
                                className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-accent/50 transition-colors text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                            >
                                <div className="flex items-center gap-1.5">
                                    <Settings className="w-3 h-3" />
                                    <span>Investigation Depth</span>
                                    <span className="text-xs opacity-60">
                                        (B:{backwardTraceHops} E:{multiHopExposureHops} F:{forwardTraceHops})
                                    </span>
                                </div>
                                <ChevronDown className={clsx("w-3 h-3 transition-transform", showAdvanced && "rotate-180")} />
                            </button>

                            {showAdvanced && (
                                <div className="px-3 pb-2 space-y-2 bg-secondary/10">
                                    {/* Backward Trace Hops */}
                                    <div>
                                        <label className="text-xs text-muted-foreground font-medium block mb-1">
                                            Backward Trace Depth
                                        </label>
                                        <div className="flex gap-1">
                                            {BACKWARD_HOP_OPTIONS.map(h => (
                                                <button
                                                    key={h}
                                                    onClick={() => setBackwardTraceHops(h)}
                                                    className={clsx(
                                                        "flex-1 px-2 py-1 rounded text-xs font-medium transition-colors",
                                                        backwardTraceHops === h
                                                            ? "bg-primary text-primary-foreground"
                                                            : "bg-secondary text-muted-foreground hover:bg-secondary/80"
                                                    )}
                                                    disabled={isRunning}
                                                >
                                                    {h} hops
                                                </button>
                                            ))}
                                        </div>
                                        <span className="text-[10px] text-muted-foreground block mt-0.5">
                                            Est. time: {backwardTraceHops <= 1 ? '5-20s' : backwardTraceHops === 3 ? '30-60s' : backwardTraceHops === 4 ? '60-90s' : '2-3min'}
                                        </span>
                                    </div>

                                    {/* Forward Trace Hops */}
                                    <div>
                                        <label className="text-xs text-muted-foreground font-medium block mb-1">
                                            Forward Trace Depth
                                        </label>
                                        <div className="flex gap-1">
                                            {FORWARD_HOP_OPTIONS.map(h => (
                                                <button
                                                    key={h}
                                                    onClick={() => setForwardTraceHops(h)}
                                                    className={clsx(
                                                        "flex-1 px-2 py-1 rounded text-xs font-medium transition-colors",
                                                        forwardTraceHops === h
                                                            ? "bg-primary text-primary-foreground"
                                                            : "bg-secondary text-muted-foreground hover:bg-secondary/80"
                                                    )}
                                                    disabled={isRunning}
                                                >
                                                    {h} hops
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Multi-Hop Exposure Hops */}
                                    <div>
                                        <label className="text-xs text-muted-foreground font-medium block mb-1">
                                            Risk Exposure Depth
                                        </label>
                                        <div className="flex gap-1">
                                            {EXPOSURE_HOP_OPTIONS.map(h => (
                                                <button
                                                    key={h}
                                                    onClick={() => setMultiHopExposureHops(h)}
                                                    className={clsx(
                                                        "flex-1 px-2 py-1 rounded text-xs font-medium transition-colors",
                                                        multiHopExposureHops === h
                                                            ? "bg-primary text-primary-foreground"
                                                            : "bg-secondary text-muted-foreground hover:bg-secondary/80"
                                                    )}
                                                    disabled={isRunning}
                                                >
                                                    {h} hops
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Preset Modes */}
                                    <div className="pt-1.5 border-t border-border/50">
                                        <label className="text-xs text-muted-foreground font-medium block mb-1">
                                            Quick Presets
                                        </label>
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => applyPreset('quick')}
                                                disabled={isRunning}
                                                className="flex-1 px-2 py-1 rounded text-xs font-medium bg-secondary text-muted-foreground hover:bg-secondary/80 transition-colors disabled:opacity-50"
                                                title="1-hop traces + hop-1 exposure — fastest triage"
                                            >
                                                ⚡ Quick (1/1/1)
                                            </button>
                                            <button
                                                onClick={() => applyPreset('standard')}
                                                disabled={isRunning}
                                                className="flex-1 px-2 py-1 rounded text-xs font-medium bg-secondary text-muted-foreground hover:bg-secondary/80 transition-colors disabled:opacity-50"
                                            >
                                                📊 Standard (4/3/4)
                                            </button>
                                            <button
                                                onClick={() => applyPreset('deep')}
                                                disabled={isRunning}
                                                className="flex-1 px-2 py-1 rounded text-xs font-medium bg-secondary text-muted-foreground hover:bg-secondary/80 transition-colors disabled:opacity-50"
                                            >
                                                🔍 Deep (6/4/6)
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Prefetch + progress — above sections for clear pipeline status */}
                    {prefetchStatus === 'loading' && (
                        <div className="px-3 pt-1.5">
                            <div className="text-xs text-muted-foreground animate-pulse">Caching transaction data...</div>
                        </div>
                    )}
                    {isRunning && (
                        <div className="px-3 pt-1.5 space-y-1">
                            <div className="h-1 bg-secondary rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                                    style={{ width: `${(progress.completed / progress.total) * 100}%` }}
                                />
                            </div>
                            {/* R-AI-02: Show what current step is doing */}
                            <div className="text-[10px] text-muted-foreground flex items-center justify-between">
                                <span>Step {progress.completed + 1} of {progress.total}</span>
                                {(() => {
                                    const currentSection = SECTIONS.find(s => sections[s.key]?.status === 'loading');
                                    return currentSection ? <span className="text-primary/70 truncate ml-2">{currentSection.label}</span> : null;
                                })()}
                            </div>
                        </div>
                    )}

                    {/* Sections */}
                    <div className="p-2 space-y-0.5">
                        {SECTIONS.map(({ key, label, icon: Icon, description }) => {
                            const section = sections[key];
                            const isExpanded = expandedKey === key;
                            const isLoading = section.status === 'loading';
                            const isLoaded = section.status === 'loaded';
                            const isError = section.status === 'error';
                            const isDegraded = isLoaded && section.health === 'degraded';

                            return (
                                <div key={key}>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => setExpandedKey(isExpanded ? null : key)}
                                            className="p-0.5 text-muted-foreground/60 hover:text-muted-foreground"
                                            disabled={section.status === 'idle'}
                                        >
                                            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                        </button>
                                        <div
                                            className={clsx(
                                                "flex-1 flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium transition-all",
                                                isLoading ? "bg-primary/5 text-primary" :
                                                    isError ? "bg-red-500/5 text-red-600 dark:text-red-400" :
                                                        isDegraded ? "bg-amber-500/10 text-amber-700 dark:text-amber-400" :
                                                            isLoaded ? "bg-emerald-500/5 text-foreground" :
                                                            "text-muted-foreground"
                                            )}
                                        >
                                            <StatusIcon status={section.status} degraded={isDegraded} />
                                            {section.status === 'idle' && <Icon className="w-3.5 h-3.5 shrink-0" />}
                                            <div className="flex-1 min-w-0">
                                                <span className="block">{label}</span>
                                                {isLoaded && section.data && (
                                                    <span className="block text-sm text-muted-foreground truncate mt-0.5">
                                                        {getSummaryLine(key, section.data)}
                                                    </span>
                                                )}
                                                {isDegraded && (
                                                    <span className="block text-sm text-amber-600 dark:text-amber-400 truncate mt-0.5">
                                                        Degraded evidence{section.degradedReason ? `: ${section.degradedReason}` : ''}
                                                    </span>
                                                )}
                                                {/* R-AI-02: Meaningful progress messages during loading */}
                                                {isLoading && (
                                                    <span className="block text-sm text-primary/70 truncate mt-0.5 animate-pulse">
                                                        {key === 'exchange_detection' ? 'Checking entity database for known exchange wallets...' :
                                                         key === 'velocity' ? 'Analyzing transaction frequency and volume patterns...' :
                                                         key === 'counterparty_triage' ? 'Ranking counterparties by volume and risk...' :
                                                         key === 'adaptive_transactions' ? 'Fetching additional transactions based on signal patterns...' :
                                                         key === 'signal_assessment' ? 'Running AI assessment across 8 signal families...' :
                                                         key === 'multi_hop_exposure' ? 'Computing risk exposure with multi-hop decay...' :
                                                         key === 'backward_trace' ? 'Tracing fund sources via BFS...' :
                                                         key === 'forward_trace' ? 'Tracing fund destinations via BFS...' :
                                                         'Processing...'}
                                                    </span>
                                                )}
                                                {isError && (
                                                    <span className="block text-sm text-red-500 truncate mt-0.5">
                                                        {section.error || 'Failed'}
                                                    </span>
                                                )}
                                            </div>
                                            {isLoading && <ElapsedTimer />}
                                            {isError && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleRefresh(key); }}
                                                    className="p-0.5 hover:bg-accent rounded shrink-0"
                                                    title="Retry this section"
                                                >
                                                    <RefreshCw className="w-3 h-3" />
                                                </button>
                                            )}
                                            {mode === 'manual' && section.status === 'idle' && !isRunning && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleRefresh(key); }}
                                                    className="px-1.5 py-0.5 text-[10px] font-medium bg-emerald-600 hover:bg-emerald-700 text-foreground rounded shrink-0"
                                                    title={`Run ${label}`}
                                                    disabled={!targetAddress}
                                                >
                                                    Run
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* SSE live progress for all sections (unified stream) */}
                                    {isLoading && section.progress && section.progress.length > 0 && (
                                        <div className="ml-5 mt-1 text-xs text-muted-foreground space-y-0.5">
                                            {/* Phase updates (trace sections) */}
                                            {section.progress.filter((p: any) => p.type === 'phase').slice(-1).map((p: any, i: number) => (
                                                <div key={`phase-${i}`} className="text-primary/70 italic">{p.data.message}</div>
                                            ))}
                                            {/* Progress updates (all sections via unified stream) */}
                                            {section.progress.filter((p: any) => p.type === 'progress').slice(-1).map((p: any, i: number) => (
                                                <div key={`prog-${i}`} className="space-y-0.5">
                                                    <div className="flex gap-2 font-mono">
                                                        {p.data.message && <span className="text-primary/70">{p.data.message}</span>}
                                                        {!p.data.message && p.data.hop != null && <span>Hop {p.data.hop}</span>}
                                                        {p.data.nodes_found > 0 && (
                                                            <span className="text-emerald-500">{p.data.nodes_found} nodes</span>
                                                        )}
                                                        {p.data.total_fetched > 0 && (
                                                            <span>{p.data.total_fetched} txs</span>
                                                        )}
                                                    </div>
                                                    {/* Rich BFS progress: nodes/edges/entities found, time estimate */}
                                                    {(p.data.addresses_investigated > 0 || p.data.nodes_found > 0 || p.data.edges_found > 0) && (
                                                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                                                            {p.data.nodes_found > 0 && <span>{p.data.nodes_found} nodes</span>}
                                                            {p.data.edges_found > 0 && <span>{p.data.edges_found} edges</span>}
                                                            {p.data.entities_found > 0 && <span className="text-emerald-500">{p.data.entities_found} entities</span>}
                                                            {p.data.elapsed_s > 0 && <span>{p.data.elapsed_s}s elapsed</span>}
                                                            {p.data.est_remaining_s != null && p.data.est_remaining_s > 0 && (
                                                                <span className="text-amber-500">~{Math.round(p.data.est_remaining_s)}s remaining</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                            {/* Hop-by-hop updates (trace sections) */}
                                            {section.progress.filter((p: any) => p.type === 'hop').map((p: any, i: number) => (
                                                <div key={`hop-${i}`} className="flex gap-2 font-mono">
                                                    <span>Hop {p.data.hop}:</span>
                                                    <span>{p.data.addresses_fetched} addr</span>
                                                    <span className="text-muted-foreground/60">({p.data.cache_hits} cached)</span>
                                                    {p.data.entities_found > 0 && (
                                                        <span className="text-emerald-500">{p.data.entities_found} entities</span>
                                                    )}
                                                    {p.data.entity_names?.length > 0 && (
                                                        <span className="text-amber-500 truncate">{p.data.entity_names.map((n: any) => safeStr(n)).filter(Boolean).join(', ')}</span>
                                                    )}
                                                </div>
                                            ))}
                                            {/* Node discovery count */}
                                            {(() => {
                                                const discovered = section.progress.filter((p: any) => p.type === 'node_discovered');
                                                return discovered.length > 0 ? (
                                                    <div className="text-emerald-500/80 font-mono">
                                                        {discovered.length} node{discovered.length !== 1 ? 's' : ''} discovered
                                                        {discovered[discovered.length - 1]?.data?.entity_name && (
                                                            <span className="text-amber-500 ml-1">
                                                                (latest: {safeStr(discovered[discovered.length - 1].data.entity_name)})
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : null;
                                            })()}
                                            {/* Decision events (pruning/expansion) */}
                                            {section.progress.filter((p: any) => p.type === 'decision').slice(-2).map((p: any, i: number) => (
                                                <div key={`dec-${i}`} className="text-amber-500/70 italic">
                                                    {p.data.action}: {p.data.reason}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Expanded detail */}
                                    {isExpanded && (isLoaded || isError) && (
                                        <div className="ml-5 mt-1 mb-2 p-2 bg-secondary/30 rounded-lg border border-border/50">
                                            {isLoaded && section.data
                                                ? renderDetail(key, section.data)
                                                : <p className="text-sm text-red-500">{section.error || 'Failed to load data.'}</p>
                                            }
                                        </div>
                                    )}
                                    {isExpanded && section.status === 'idle' && (
                                        <div className="ml-5 mt-1 mb-2">
                                            <p className="text-sm text-muted-foreground">{description}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Circulation & Pass-Through (cycle_detector) — conserved, time-
                        ordered, single-asset corridors, grouped by pivot. Not an
                        INVESTIGATION_KEYS section; read from investigation.circulation. */}
                    {circulation && Array.isArray(circulation.findings) && circulation.findings.length > 0 && (
                        <div className="mt-2 px-1">
                            <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium bg-violet-500/5 text-foreground">
                                <Repeat className="w-3.5 h-3.5 shrink-0 text-violet-500" />
                                <div className="flex-1 min-w-0">
                                    <span className="block">Circulation &amp; Pass-Through</span>
                                    <span className="block text-sm text-muted-foreground truncate mt-0.5">
                                        {circulation.findings.length} conserved {circulation.findings.length === 1 ? 'corridor' : 'corridors'}
                                        {circulation.summary?.passthrough_corridors != null && ` · ${circulation.summary.passthrough_corridors} pass-through`}
                                        {circulation.summary?.validated_roundtrips ? ` · ${circulation.summary.validated_roundtrips} round-trip` : ''}
                                    </span>
                                </div>
                            </div>
                            <div className="ml-5 mt-1 space-y-2">
                                {circByPivot.map(([pivot, finds]) => (
                                    <div key={pivot} className="p-2 bg-secondary/30 rounded-lg border border-border/50">
                                        <div className="flex items-center gap-1.5 text-[11px] font-medium text-violet-600 dark:text-violet-300">
                                            <Repeat className="w-3 h-3 shrink-0" />
                                            <span className="font-mono truncate">pivot {pivot.length > 14 ? `${pivot.slice(0, 10)}…${pivot.slice(-4)}` : pivot}</span>
                                            <span className="text-muted-foreground">· {finds.length} {finds.length === 1 ? 'corridor' : 'corridors'}</span>
                                        </div>
                                        <div className="mt-1 space-y-1">
                                            {finds.slice(0, 6).map((f: any) => (
                                                <div key={f.id} className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                                    <span className="font-mono">{(f.origin || '?').slice(0, 6)}…→{(f.terminus || '?').slice(0, 6)}…</span>
                                                    {f.bottleneck_usd != null && (
                                                        <span className="text-emerald-600 dark:text-emerald-400 font-medium">${Math.round(f.bottleneck_usd).toLocaleString()}</span>
                                                    )}
                                                    {f.token && <span>{f.token}</span>}
                                                    {f.total_dwell_s != null && (
                                                        <span>{f.total_dwell_s < 3600 ? `${Math.round(f.total_dwell_s / 60)}m` : `${(f.total_dwell_s / 3600).toFixed(1)}h`} dwell</span>
                                                    )}
                                                    {f.typology && (
                                                        <span className="px-1 rounded bg-violet-500/10 text-violet-600 dark:text-violet-300">{String(f.typology).replace(/_/g, ' ')}</span>
                                                    )}
                                                    {f.involves_subject && (
                                                        <span className="px-1 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400">subject</span>
                                                    )}
                                                    {f.confidence != null && <span className="text-muted-foreground/70">conf {f.confidence}</span>}
                                                </div>
                                            ))}
                                            {finds.length > 6 && (
                                                <div className="text-[10px] text-muted-foreground/60">+{finds.length - 6} more…</div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Guided mode: Continue button after signal assessment */}
                    {guidedPaused && !isRunning && (
                        <div className="px-3 py-2 border-t border-border/30 bg-amber-500/5">
                            <div className="text-xs text-amber-600 dark:text-amber-400 mb-1.5">
                                Signal assessment complete. Review results above, then continue with remaining analyses.
                            </div>
                            <button
                                onClick={handleGuidedContinue}
                                className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-foreground transition-colors"
                            >
                                <Play className="w-3.5 h-3.5" />
                                Continue Investigation
                            </button>
                        </div>
                    )}

                </div>{/* end overflow container */}

                    {/* Footer actions — outside overflow so always visible */}
                    <div className="px-3 pb-2.5 pt-1 border-t border-border/20 shrink-0">
                        {allIdle && !isRunning && !guidedPaused && mode !== 'manual' && (
                            <div className="space-y-1.5">
                                <button
                                    onClick={handleRun}
                                    disabled={!targetChain || !targetAddress}
                                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Play className="w-3.5 h-3.5" />
                                    {mode === 'guided' ? 'Start Assessment' : 'Run full Auto-Investigate'}
                                </button>
                                {/* Issue A — surface previously-archived report on reload
                                    (idle state) so the user can re-open without re-running. */}
                                {savedReport && onOpenSavedReport && (
                                    <button
                                        onClick={() => onOpenSavedReport(savedReport.id)}
                                        className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-medium text-primary bg-primary/10 hover:bg-primary/20 border border-primary/30 transition-colors"
                                        title={savedReport.title}
                                    >
                                        <FileText className="w-3 h-3" />
                                        <span>View previously saved report</span>
                                        {savedReport.generatedAt && (
                                            <span className="text-muted-foreground/80">
                                                · {new Date(savedReport.generatedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                                            </span>
                                        )}
                                    </button>
                                )}
                            </div>
                        )}

                        {isRunning && (
                            <button
                                onClick={cancel}
                                className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-secondary hover:bg-secondary/80 text-foreground border border-border transition-colors"
                            >
                                <XCircle className="w-3.5 h-3.5" />
                                Cancel ({progress.completed}/{progress.total} complete)
                            </button>
                        )}

                        {isComplete && !isRunning && !allIdle && sections.signal_assessment.status !== 'loading' && (
                            <div className="space-y-1.5">
                                {hasQualityIssues && !nodeMismatch && (
                                    <div className="p-2 rounded-lg border border-amber-500/30 bg-amber-500/10 text-xs text-amber-700 dark:text-amber-400">
                                        Investigation quality issues detected.
                                        {degradedSectionKeys.length > 0 && (
                                            <> Degraded: {degradedSectionKeys.join(', ')}.</>
                                        )}
                                        {erroredSectionKeys.length > 0 && (
                                            <> Errors: {erroredSectionKeys.join(', ')}.</>
                                        )}
                                        {' '}A limited summary report will be generated instead of a full LE report.
                                    </div>
                                )}
                                {/* Per-node guard: while a different canvas node is
                                    selected, hide "View LE Report" — it would render the
                                    PREVIOUS target's data. Show Switch (primary) + the
                                    selected node's saved report (if any) instead. */}
                                {!nodeMismatch && (
                                    <button
                                        onClick={handleViewLeReport}
                                        data-tour="le-report"
                                        className={clsx(
                                            "w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-foreground transition-colors",
                                            hasQualityIssues ? "bg-amber-600 hover:bg-amber-700" : "bg-primary hover:bg-brand-gold-hover text-primary-foreground",
                                        )}
                                    >
                                        {isSavingReport ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                                        {isSavingReport ? 'Saving Report…' : hasQualityIssues ? 'View Limited Report' : 'View LE Report'}
                                    </button>
                                )}

                                {nodeMismatch && address && (
                                    <button
                                        onClick={() => handleSwitchToSelectedNode()}
                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white transition-colors"
                                        title={`Discard current results and target ${address.slice(0, 8)}…${address.slice(-4)}`}
                                    >
                                        <ArrowUpRight className="w-3.5 h-3.5" />
                                        Switch to selected node
                                    </button>
                                )}

                                {/* Saved-per-node report. Always reflects the SELECTED
                                    node (savedAutoInvestigateReportFor in Workbench keys
                                    on selectedNode.id), so it remains correct even when
                                    the in-memory results belong to another node. */}
                                {savedReport && onOpenSavedReport && (
                                    <button
                                        onClick={() => onOpenSavedReport(savedReport.id)}
                                        className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-medium text-primary bg-primary/10 hover:bg-primary/20 border border-primary/30 transition-colors"
                                        title={savedReport.title}
                                    >
                                        <FileText className="w-3 h-3" />
                                        <span>{nodeMismatch ? 'View saved report for selected node' : 'View saved report'}</span>
                                        {savedReport.generatedAt && (
                                            <span className="text-muted-foreground/80">
                                                · {new Date(savedReport.generatedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                                            </span>
                                        )}
                                    </button>
                                )}

                                {/* Add to Graph — backward trace.
                                    Suppressed under nodeMismatch because the trace
                                    data belongs to the previous target, not the
                                    selected canvas node. */}
                                {!nodeMismatch && traceData && (traceNodeCount > 0 || traceEdgeCount > 0) && onAddToGraph && (
                                    <button
                                        onClick={handleAddTraceToGraph}
                                        disabled={traceAdded}
                                        className={clsx(
                                            "w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
                                            traceAdded
                                                ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 cursor-default"
                                                : "bg-secondary hover:bg-secondary/80 text-foreground border border-border"
                                        )}
                                    >
                                        {traceAdded ? (
                                            <>
                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                Added to Graph
                                            </>
                                        ) : (
                                            <>
                                                <Network className="w-3.5 h-3.5" />
                                                Add {traceNodeCount} nodes & {traceEdgeCount} edges to Graph
                                            </>
                                        )}
                                    </button>
                                )}

                                {/* Add to Graph — forward trace (suppressed on mismatch). */}
                                {!nodeMismatch && fwdTraceData && (fwdTraceNodeCount > 0 || fwdTraceEdgeCount > 0) && onAddToGraph && (
                                    <button
                                        onClick={handleAddForwardTraceToGraph}
                                        disabled={forwardTraceAdded}
                                        className={clsx(
                                            "w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
                                            forwardTraceAdded
                                                ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 cursor-default"
                                                : "bg-secondary hover:bg-secondary/80 text-foreground border border-border"
                                        )}
                                    >
                                        {forwardTraceAdded ? (
                                            <>
                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                Forward Trace Added
                                            </>
                                        ) : (
                                            <>
                                                <Network className="w-3.5 h-3.5" />
                                                Add {fwdTraceNodeCount} fwd-trace nodes & {fwdTraceEdgeCount} edges
                                            </>
                                        )}
                                    </button>
                                )}

                                {/* Add to Graph — top counterparties (suppressed on mismatch). */}
                                {!nodeMismatch && counterpartyCount > 0 && onAddToGraph && (
                                    <button
                                        onClick={handleAddCounterpartiesToGraph}
                                        disabled={counterpartiesAdded}
                                        className={clsx(
                                            "w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
                                            counterpartiesAdded
                                                ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 cursor-default"
                                                : "bg-secondary hover:bg-secondary/80 text-foreground border border-border"
                                        )}
                                    >
                                        {counterpartiesAdded ? (
                                            <>
                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                Counterparties Added
                                            </>
                                        ) : (
                                            <>
                                                <Users className="w-3.5 h-3.5" />
                                                Show {counterpartyCount} Top Counterparties
                                            </>
                                        )}
                                    </button>
                                )}

                                {/* R-AI-21: Suggested Next Steps (collapsible, collapsed by default) */}
                                {suggestedNextSteps && isComplete && !isRunning && (
                                    <div className="mt-1">
                                        <button
                                            onClick={() => setNextStepsExpanded(prev => !prev)}
                                            className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground hover:text-foreground transition-colors"
                                        >
                                            {nextStepsExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                            Suggested Next Steps ({suggestedNextSteps.length})
                                        </button>
                                        {nextStepsExpanded && (
                                            <div className="space-y-1 mt-1.5">
                                                {suggestedNextSteps.map((step, idx) => (
                                                    <div key={idx} className={`flex items-start gap-2 px-2.5 py-1.5 rounded text-xs border ${
                                                        step.priority === 'high' ? 'bg-red-500/5 border-red-500/20 text-red-700 dark:text-red-400' :
                                                        step.priority === 'medium' ? 'bg-amber-500/5 border-amber-500/20 text-amber-700 dark:text-amber-400' :
                                                        'bg-secondary/50 border-border text-muted-foreground'
                                                    }`}>
                                                        <span className="font-bold shrink-0 mt-px">{idx + 1}.</span>
                                                        <span className="leading-relaxed">{step.text}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!nodeMismatch && targetChain && targetAddress && (
                                    <InvestigationFeedback
                                        chain={targetChain}
                                        address={targetAddress}
                                        investigationData={exportForSave()}
                                    />
                                )}

                                {/* Decision actions — 2-column grid */}
                                <div className="flex items-center gap-2 py-1">
                                    <div className="flex-1 h-px bg-border/50" />
                                    <span className="text-[10px] text-muted-foreground uppercase font-bold">Actions</span>
                                    <div className="flex-1 h-px bg-border/50" />
                                </div>
                                <div className="grid grid-cols-2 gap-1.5">
                                    {(escalation === 'ESCALATE' || escalation === 'INVESTIGATE') && onNotice && (
                                        <button
                                            onClick={onNotice}
                                            className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30 transition-colors"
                                        >
                                            <FileText className="w-3.5 h-3.5 shrink-0" />
                                            Issue Notice
                                        </button>
                                    )}
                                    {onWatchlist && (
                                        <button
                                            onClick={onWatchlist}
                                            className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium bg-secondary hover:bg-secondary/80 text-foreground border border-border transition-colors"
                                        >
                                            <Eye className="w-3.5 h-3.5 shrink-0" />
                                            Watchlist
                                        </button>
                                    )}
                                    {onSave && (
                                        <button
                                            onClick={onSave}
                                            className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors"
                                        >
                                            <Save className="w-3.5 h-3.5 shrink-0" />
                                            Save
                                        </button>
                                    )}
                                    <button
                                        onClick={handleRun}
                                        disabled={!targetChain || !targetAddress}
                                        className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium bg-secondary hover:bg-secondary/80 text-foreground border border-border transition-colors"
                                    >
                                        <RefreshCw className="w-3.5 h-3.5 shrink-0" />
                                        Re-run
                                    </button>
                                </div>
                            </div>
                        )}

                        <PromoteToCaseControl
                            caseId={caseId}
                            investigationId={caseId ? new URLSearchParams(window.location.search).get('investigation_id') : null}
                            chain={targetChain}
                            subject={investigation?.investigatedAddress || targetAddress}
                            isRunning={isRunning}
                            isComplete={isComplete && !nodeMismatch}
                            spinePromotion={investigation?.spinePromotion ?? null}
                            getResults={() => investigation?.exportResultsForPromotion?.() ?? null}
                        />
                    </div>
                </>
            )}
        </div>
    );
};
